import Vue from 'vue'
import Vuex from 'vuex'
import { composerSkillPresets, composerModelPresets } from '../data/presets'
import { telegramSessionProfiles, sessionTopicPages, agentInspectorProfiles, agentTasksData, agentSessionProfiles, groupWorkspaceCloudComputers } from '../data/sessions'
import {
  normalizeDemoQuery,
  shortenDemoQuery,
  extractDemoTopic,
  makeReportFileName,
  getStorageItem,
  setStorageItem
} from '../utils/helpers'
import { getAuthState } from '../utils/auth'
import { api } from '../utils/api'

// ── 工具函数 ──────────────────────────────────────────────

function formatTime(isoStr) {
  if (!isoStr) return ''
  try {
    const d = new Date(isoStr)
    return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
  } catch { return '' }
}

function formatRelativeTime(isoStr) {
  if (!isoStr) return ''
  try {
    const d = new Date(isoStr)
    const now = new Date()
    const diffMs = now - d
    if (diffMs < 60000) return '刚刚'
    if (diffMs < 3600000) return Math.floor(diffMs / 60000) + '分钟前'
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
    }
    return d.toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' })
  } catch { return '' }
}

function extractLastMessage(c) {
  const lm = c.lastMessage || c.last_message
  // lastMessage 为 null 或不存在时直接返回空
  if (lm === null || lm === undefined) return ''
  if (typeof lm === 'string') return lm
  // 文本消息： body.text
  if (lm.body) {
    if (lm.body.text) return lm.body.text
    if (lm.body.files && lm.body.files.length) {
      const f = lm.body.files[0]
      return f.name || '[文件]'
    }
  }
  return lm.text || lm.content || ''
}

function extractLastMessageFromTopic(t) {
  const lm = t.lastMessage || t.last_message
  if (!lm) return ''
  if (lm.body && lm.body.text) return lm.body.text
  if (lm.body && lm.body.files && lm.body.files.length) {
    const f = lm.body.files[0]
    return f.name || '[文件]'
  }
  return ''
}

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return ''
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

// 根据文件扩展名推断 kind（当 mimeType 为 application/octet-stream 时兜底）
const AUDIO_EXTS = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'wma', 'm4a']
const VIDEO_EXTS = ['mp4', 'webm', 'mkv', 'avi', 'mov']
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']
function guessKindByExt(fileName) {
  const ext = (fileName || '').split('.').pop().toLowerCase()
  if (AUDIO_EXTS.includes(ext)) return 'audio'
  if (VIDEO_EXTS.includes(ext)) return 'video'
  if (IMAGE_EXTS.includes(ext)) return 'image'
  return null
}

// 消息按真实顺序排序：seq 优先（后端发送序号最权威），其次 created_at，最后 id 兜底稳定
// created_at 解析失败（NaN）按 0 处理，避免 sort 顺序乱
function compareMessages(a, b) {
  const sa = a.seq || 0
  const sb = b.seq || 0
  if (sa !== sb) return sa - sb
  const taRaw = new Date(a.created_at || a.createdAt || 0).getTime()
  const tbRaw = new Date(b.created_at || b.createdAt || 0).getTime()
  const ta = isNaN(taRaw) ? 0 : taRaw
  const tb = isNaN(tbRaw) ? 0 : tbRaw
  if (ta !== tb) return ta - tb
  return String(a.id || '').localeCompare(String(b.id || ''))
}

// 将 API 消息对象映射为 TelegramChat 所需的格式
function mapApiMessageToUi(m, currentUserId) {
  // 已 mapped（来自 WS_NEW_MESSAGE 写入 raw 的消息）直接返回
  if (m && m._isMapped) return m
  const sender = m.sender || {}
  // 按优先级尝试所有可能的字段名
  const senderId = sender.id || sender.userId || m.senderId || m.sender_id ||
    m.userId || m.user_id || m.agentId || m.agent_id || ''
  const senderType = sender.type || m.senderType || m.sender_type || 'user'
  // agent 消息永远在左侧；user 消息比对 currentUserId
  const isAgent = senderType === 'agent'
  const isMe = !isAgent && !!(currentUserId && senderId && senderId === currentUserId)
  const messageType = m.messageType || m.message_type || 'text'
  const body = m.body || {}

  // 系统消息（群聊通知）
  if (senderType === 'system' || senderId === 'system') {
    return {
      id: m.id,
      seq: m.seq || 0,
      side: 'center',
      kind: 'system',
      text: body.text || m.text || '',
      time: formatTime(m.created_at || m.createdAt),
      createdAt: m.created_at || m.createdAt || '',
      _isMapped: true
    }
  }

  // 文件/图片消息（防御：messageType 缺失或值不标准时，只要 body.files 有内容也按文件消息解析）
  if (messageType === 'file' || (body.files && body.files.length > 0)) {
    const files = body.files || []
    const firstFile = files[0] || {}
    const mimeType = firstFile.mimeType || body.mimeType || ''
    const fileType = firstFile.type || ''

    let kind
    if (files.length > 1) {
      // multi-file: always use 'multi' so bubble renderer uses the multi-file list
      kind = 'multi'
    } else if (fileType === 'image' || mimeType.startsWith('image/')) {
      kind = 'image'
    } else if (fileType === 'video' || mimeType.startsWith('video/')) {
      kind = 'video'
    } else if (fileType === 'audio' || mimeType.startsWith('audio/')) {
      kind = 'audio'
    } else if (fileType === 'markdown') {
      kind = 'document'
    } else if (fileType === 'document') {
      kind = 'document'
    } else {
      // 兜底：根据文件扩展名判断
      kind = guessKindByExt(firstFile.name || firstFile.fileName) || 'file'
    }

    const fileSize = firstFile.size || firstFile.fileSize || body.fileSize || 0
    let fileName = firstFile.name || firstFile.fileName || body.fileName || ''
    
    // 如果 name 缺失，从 URL 提取文件名作为备选
    if (!fileName && firstFile.url) {
      try {
        const url = new URL(firstFile.url)
        const pathname = url.pathname
        // 提取最后一个路径段作为文件名，去掉时间戳前缀 (例如: 20260326042134_c23eda30.txt)
        const parts = pathname.split('/')
        const lastPart = parts[parts.length - 1]
        if (lastPart && lastPart.includes('_')) {
          // 格式: timestamp_hash.ext，保留后面的部分
          const segments = lastPart.split('_')
          if (segments.length >= 2) {
            // 取最后两段: hash.ext
            fileName = segments.slice(-2).join('_')
          }
        } else if (lastPart) {
          fileName = lastPart
        }
      } catch (e) {
        // URL 解析失败，使用默认名称
        fileName = '文件'
      }
    }
    
    const ext = fileName.split('.').pop().toLowerCase()

    return {
      id: m.id,
      seq: m.seq || 0,
      side: isMe ? 'right' : 'left',
      name: sender.name || m.sender_name || m.senderName || '',
      agentTag: isAgent ? 'Agent' : null,
      text: body.caption || body.text || m.text || '',
      time: formatTime(m.created_at || m.createdAt),
      createdAt: m.created_at || m.createdAt || '',
      avatarText: (sender.name || m.sender_name || '?').slice(0, 1),
      avatarImage: sender.avatarUrl || null,
      avatarColorRgb: sender.avatarColorRgb || '',
      kind,
      imageUrl: kind === 'image' ? (firstFile.url || body.url || '') : '',
      fileUrl: firstFile.url || body.url || '',
      fileName,
      fileSize,
      fileSizeStr: formatFileSize(fileSize),
      mimeType,
      ext,
      files,
      rawText: body.rawText || body.raw_text || m.rawText || m.raw_text || '',
      mentions: m.mentions || [],
      ownerName: isAgent ? (sender.ownerName || sender.owner_name || '') : '',
      readCount: m.readCount || 0,
      memberCount: m.memberCount || 0,
      imageWidth: firstFile.width || 0,
      imageHeight: firstFile.height || 0,
      replyTo: m.replyTo ? {
        id: m.replyTo.id,
        name: (m.replyTo.sender && m.replyTo.sender.name) || '',
        text: ((m.replyTo.text || (m.replyTo.body && m.replyTo.body.text)) || '').replace(/\[@#\d+\]/g, '@'),
        messageType: m.replyTo.messageType || 'text',
        files: m.replyTo.files || (m.replyTo.body && m.replyTo.body.files) || null,
        agentTag: (m.replyTo.sender && m.replyTo.sender.type === 'agent') ? 'Agent' : null
      } : null,
      _isMapped: true
    }
  }

  // rawText 保留用于 @ 高亮判断，text 直接用 body.text（后端已替换好 @名字）
  const rawText = body.rawText || body.raw_text || m.rawText || m.raw_text || ''
  const apiMentions = m.mentions || []
  let text = body.text || m.text || m.content || ''
  // 思考过程内容
  const thinking = body.thinking || body.reasoning || m.thinking || m.reasoning || ''

  return {
    id: m.id,
    seq: m.seq || 0,
    side: isMe ? 'right' : 'left',
    name: sender.name || m.sender_name || m.senderName || '',
    agentTag: isAgent ? 'Agent' : null,
    // 让聊天气泡能根据 agent 当前状态(idle/busy/disabled) 渲染不同徽标 — 群里有多个 agent 时只能按 agentId 查
    agentId: isAgent ? senderId : null,
    ownerName: isAgent ? (sender.ownerName || sender.owner_name || '') : '',
    text,
    thinking,
    rawText,
    mentions: apiMentions,
    isMentioned: !!(currentUserId && apiMentions.some(mn => mn.id === currentUserId)),
    time: formatTime(m.created_at || m.createdAt),
    createdAt: m.created_at || m.createdAt || '',
    avatarText: (sender.name || m.sender_name || '?').slice(0, 1),
    avatarImage: sender.avatarUrl || null,
    avatarColorRgb: sender.avatarColorRgb || '',
    skills: (m.skills || []).map(s => ({ id: s.id, name: s.name })),
    replyTo: m.replyTo ? {
      id: m.replyTo.id,
      name: (m.replyTo.sender && m.replyTo.sender.name) || '',
      text: (m.replyTo.text || '').replace(/\[@#\d+\]/g, '@'),
      messageType: m.replyTo.messageType || 'text',
      files: m.replyTo.files || null,
      agentTag: (m.replyTo.sender && m.replyTo.sender.type === 'agent') ? 'Agent' : null
    } : null,
    kind: 'text',
    readCount: m.readCount || 0,
    memberCount: m.memberCount || 0,
    _isMapped: true
  }
}

// 校验 currentTopicKey 是否属于指定 conversationId
// 防止切换会话时残留的 topicKey 被误带到请求体里
function _agentKey(conversationId, topicId) {
  return topicId ? conversationId + ':' + topicId : conversationId
}

function _safeTopicKey(state, conversationId) {
  const topicKey = state.currentTopicKey
  if (!topicKey || !conversationId) return undefined
  const item = state.sessionItems.find(s => s.conversationId === conversationId)
  if (!item || !item.topicKey) return undefined
  const page = state.sessionTopicPages[item.topicKey]
  if (!page || !Array.isArray(page.topics)) return undefined
  return page.topics.some(t => t.key === topicKey) ? topicKey : undefined
}

// 从 JWT token 解码用户 ID（无需网络请求）
function getUserIdFromJwt() {
  try {
    const raw = localStorage.getItem('mcn_auth')
    const s = raw ? JSON.parse(raw) : null
    if (!s || !s.token) return null
    const parts = s.token.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    return payload.sub || payload.userId || payload.user_id || payload.id || null
  } catch { return null }
}

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    // 企业入职审核待处理数(FuncBar 企业图标小红点 + TeamView 左 nav badge 共用)
    teamPendingCount: 0,
    // App layout
    funcbarCollapsed: false,
    funcbarPreviewHover: false,
    workspaceOpen: true,
    workspaceWidth: null,
    chatSearchOpen: false,
    workspaceScene: 'desktop', // 'desktop' | 'processing' | 'result' | 'google'
    workspaceFloat: false,
    workspaceTakeover: false,
    groupCloudFloat: false,
    groupCloudFloatMode: null,
    currentView: 'messages', // 'messages' | 'experts' | 'teams'

    // Session state
    currentSessionKey: null,
    chatLoading: false,
    chatScrollPositions: {},  // { [key]: { scrollTop, atBottom } } 跨组件生命周期保持
    currentSessionType: 'agent',
    currentSessionFilter: 'all',
    sessionSearchQuery: '',
    sessionsPanelCollapsed: false,
    topicsViewOpen: false,
    currentTopicsPageKey: null,

    // Sessions data
    sessionItems: [],

    // Telegram data
    telegramSessionProfiles,
    sessionTopicPages,
    currentTopicKey: null,
    currentTelegramPrompt: '',
    telegramPendingReplies: {},
    currentTelegramSentReply: null,

    // Main state
    mainState: 'empty', // 'empty' | 'processing' | 'result'
    fakeFlowRunning: false,

    // Workspace content
    workspaceQuery: '',
    workspaceBrowserAddress: '桌面',
    workspaceBrowserTitle: '自主Agent有哪些？',
    workspaceBrowserDesc: '',
    workspaceResultData: {
      fileName: 'Autonomous_Agents_Report.md',
      title: '自主Agent调研报告',
      summary: '',
      coreHeading: '自主Agent的定义与核心特征',
      sectionTitle: '主流自主Agent分类与产品介绍',
      sectionDesc: ''
    },
    workspaceFile: null,  // { url, kind, fileName, mimeType, fileSize } — 当前工作区预览的文件

    // 消息置顶（topic 级，key = `${convId}::${topicId || 'default'}`）
    conversationPinnedMessages: {}, // { [key]: pinItem[] 倒序按 pinnedAt }
    pinnedBarDismissed: {},         // { [key]: true } session 级 dismiss，不持久化
    pinnedPanelOpen: false,         // 全局 panel 开关（同时只显示当前会话/话题的 panel）

    // Conversation agents (VNC)
    conversationAgents: {}, // { [conversationId]: [...agents] }
    wechatBindings: {},     // { [agentId]: { bound, binding } | null }
    // 2026-05-28 起 DELETE /agents/:id 改为停用语义；WS agent.disabled 事件回写此 tick，触发 WorkspaceView/详情灰显
    lastAgentDisabled: null, // { agentId, agentName, ownerId, ownerName, disabledAt, ts }
    conversationHasMoreAfter: {}, // { [conversationId]: true } 列表末尾还可以用 after 接口向更新方向加载（jumpToMessage 后置 true，加载到最新置 false）
    conversationLoadingAfter: {}, // { [conversationId]: true } after 加载中防抖锁
    conversationGapAfterId: {}, // { [conversationId]: msgId } 下一次 after 锚点 id（跳转后明确记录 gap 边界，不靠 seq 跳跃检测）
    conversationLatestSegmentIds: {}, // { [conversationId]: Set<id> } 跳转前的"最新段" id 集合，用来判断 after 拉到的消息是否已衔接

    // Conversation files (API)
    conversationFiles: {},       // { [conversationId]: [...files] }
    topicFolderTree: {},         // { [topicId]: { folders, topicId } }
    totalUnread: 0,              // account-level unread from /unread-count
    onlineMembersRefreshAt: 0,   // timestamp bump to trigger online count refresh
    onlineCountVersion: 0,       // 每次邀请成员后自增，触发 TelegramChat 刷新在线人数
    conversationOnlineCounts: {},  // { conversationId: totalOnlineCount }
    unreadByConversation: {},    // { [conversationId]: count }
    conversationFilesTotal: {},  // { [conversationId]: total }
    conversationFilesLoading: false,
    conversationArtifactFiles: {},  // { [conversationId]: [...files] } — 产物文件（type=agent）
    fileVersionsData: {},        // { [fileId]: { stats, dailyVersions } }
    fileVersionsLoading: false,

    // Conversation topics (API)
    conversationTopics: {},      // { [conversationId]: [...topics] }

    // Group workspace
    currentGroupWorkspaceTab: 'cloud',
    currentGroupWorkspaceSessionKey: null,
    currentGroupWorkspaceTopicKey: null,
    currentGroupWorkspaceSelections: { artifacts: null, files: null, changes: null, cloud: null },
    currentGroupWorkspacePreview: null,
    fullscreenPreviewItem: null,
    // 全屏图片左右切换画廊（微信式）：已加载窗口 + 绝对下标模型
    // loaded[absIndex - baseIndex] = 该绝对下标的图；cur 为当前图绝对下标；计数 cur+1 / total
    fullscreenGallery: null,
    iframeModal: { open: false, url: '', title: '' },
    groupWorkspaceItems: [],

    // Agent modals/popovers
    agentInsightModal: { open: false, type: 'soul', sessionKey: '' },
    agentDetailPopover: { open: false, sessionKey: '', x: null, y: null },
    agentTaskBubble: { open: false, sessionKey: '', x: null, y: null },
    agentTaskPreview: { open: false, sessionKey: '', id: '', detail: null, detailLoading: false },
    contextUsageDisplay: {},    // { [conversationId]: 'xxx / xxxk' }
    agentScheduledTasks: {},    // { [conversationId]: [...tasks] }
    agentScheduledTasksLoading: false,
    agentPromptSections: {},    // { [conversationId]: { identity, soul, persona, style, system, memory, system_prompt } }
    agentPromptSectionsLoading: false,
    agentSoulOverrides: {},
    agentMemoryOverrides: {},
    agentSessionProfiles,
    groupWorkspaceCloudComputers,

    // Telegram context menus
    telegramGroupMenu: { open: false, x: 0, y: 0, sessionKey: '' },
    telegramReplyMenu: { open: false, x: 0, y: 0, name: '', text: '' },
    telegramEmojiMenu: { open: false, x: 0, y: 0, context: '', selStart: 0, selEnd: 0, val: '' },
    telegramMentionMenu: { open: false, x: 0, y: 0, context: '', members: [], atStart: 0, query: '', val: '' },
    mentionActiveIndex: 0,
    forwardModal: { open: false, senderName: '', text: '', kind: 'text', imageUrl: '', fileName: '', files: null },
    multiSelectMode: false,
    multiSelectedMessages: [],

    // Group dialog
    vncModal: { open: false, url: '', agentName: '', takeover: false },
    cloudActionBusy: '',  // '' | 'OpenClaw 正在重启中…' | 'OpenClaw 正在恢复出厂设置中…'
    pendingVncAgentId: null,
    pendingVncTakeover: false,

    groupDialog: { open: false, mode: null, sessionKey: '' },
    groupDialogData: {},
    createdGroupCount: 0,

    // Skill & model picker
    selectedSkillIds: [],  // legacy
    sessionSkillMap: {},  // sessionKey(:topicKey) -> skillId[]
    sessionCommandMap: {},  // sessionKey(:topicKey) -> cmd string
    selectedModel: 'FengBaoAuto',
    sessionModelMap: {},
    modelList: [],          // 模型 ID 列表（兼容 ModelPickerPopover）
    modelDetails: [],       // 完整模型对象列表（含 thinks, defaultThink）
    commandList: [],      // GET /agents/capabilities 返回的 total 列表
    commandListLoading: false,
    agentSkillList: [],   // GET /agents/capabilities 返回的 skills 列表（转换后）
    agentSkillLoading: false,
    lastAgentThinking: null, // WS_AGENT_DONE 时暂存 thinking，供 GroupWorkspacePanel 追加
    notificationSettings: {
      notificationMessage: true,
      notificationTaskComplete: true,
      notificationErrorAlert: true,
      dndEnabled: false,
      dndStart: '22:00',
      dndEnd: '08:00'
    },
    // 快捷键设置
    hotkeySettings: JSON.parse(localStorage.getItem('arp_hotkey_settings') || 'null') || { send: { key: 'Enter' }, newline: { ctrl: true, key: 'Enter' } },
    skillPickerOpen: false,
    modelPickerOpen: false,
    skillPickerTriggerRect: null,
    modelPickerTriggerRect: null,
    thinkingDepth: 'medium',
    thinkingDepthPickerOpen: false,
    thinkingDepthPickerRect: null,

    // ── API 会话 & 消息状态 ──────────────────────────────
    localReadTopicIds: {}, // 本次会话中用户主动已读的 topicId 集合 { [topicId]: true }
    conversations: [],              // API 返回的原始会话列表
    conversationMessages: {},       // { [conversationId]: UIMessage[] } (乐观更新的临时消息)
    conversationRawMessages: {},    // { [conversationId]: RawMessage[] } (API 原始消息，用于响应式重映射)
    conversationMappedMessages: {}, // { [conversationId]: UiMessage[] } (已映射缓存，避免 getter 每次重算)
    conversationMessagesHasMore: {},// { [conversationId]: boolean }
    conversationMessagesLoading: {},// { [conversationId]: boolean }
    mentionedMsgIds: {},             // { [conversationId]: string[] } — 被@的消息ID列表
    mentionJumpIndex: {},            // { [conversationId]: number } — 当前跳转到第几个
    firstUnreadMessageId: {},        // { [conversationId]: string | null } — 第一条未读消息ID
    mentionableMembers: {},         // { [conversationId]: [{ id, name, type, ... }] }
    availableMembers: {},           // { [conversationId]: [{ id, name, ... }] } — 可邀请成员
    teamMembers: [],                // 团队成员列表（用于创建群聊选人）
    directContacts: [],             // 联系人列表（用于发起私聊选人，仅 account 类型）
    pendingMentions: {},            // { [name]: { id, type, name } } — 输入框中已选的@成员
    currentUserId: (() => {
      // 启动时从 localStorage 恢复 userId（登录时写入）
      try {
        const s = getAuthState()
        return s ? (s.userId || null) : null
      } catch { return null }
    })(),            // 当前登录用户 ID（用于判断消息方向）
    conversationsLoaded: false,     // 是否已加载过会话列表

    // ── WebSocket 状态 ───────────────────────────────────
    agentTyping: {},                // { [convId 或 convId:topicId]: { agentId, agentName } | null }
    agentStreamingMessages: {},     // { [convId 或 convId:topicId]: { content, agentId, inReplyTo } | null }
    groupPlans: {},                 // { [convId:topicId:agentId]: { agentId, agentName, avatarUrl, metadata } }

    // Auth state (loaded from localStorage on init)
    auth: (() => {
      const s = getAuthState()
      return {
        loggedIn: !!(s && s.token),
        phone: s ? s.phone : '',
        name: s ? s.name : '',
        avatarUrl: s ? (s.avatarUrl || '') : '',
        avatarColorRgb: s ? (s.avatarColorRgb || '') : '',
        role: s ? s.role : '',
        positionId: s ? (s.positionId || '') : '',
        positionName: s ? (s.positionName || '') : '',
        assistantName: s ? s.assistantName : '',
        setupStep: s ? (s.setupStep || 0) : 0
      }
    })(),

    // Context menu
    sessionContextMenu: { open: false, x: 0, y: 0, sessionKey: null },
    sessionAddMenuOpen: false,

    // Processing progress content
    processingContent: {
      userBubble: '自主Agent有哪些？',
      agentReply: '好的，我将为您调研并整理目前主流的自主Agent（Autonomous Agents），请稍候。',
      progressTitle: '搜索：自主Agent',
      searchDesc: '目前已向用户确认想研究自主Agent，下一步将会直接搜集主流自主Agent的主要信息与特点。',
      searchChip1: '搜索目前主流的自主Agent列表和分类',
      searchChip2: '访问相关网页以获取主流自主Agent的详细功能和最新进展',
      searchChip3: '搜索 吴彦祖Agent 和 Devin AI 的最新信息及竞争对手',
      resultDesc: '已整理出主流自主Agent（如 LangGraph、AutoGen、CrewAI、OpenAI Agents SDK、MetaGPT 等）的功能特点',
      resultChip1: '阅读之前保存的网页内容，获取自主Agent相关框架与方案的详细信息',
      resultChip2: '创建Markdown文件，用于整理自主Agent的调研结果'
    },

    // Inspector data
    agentInspectorProfiles,
    agentTasksData,

    // Composer inputs (tracked as reactive)
    composerInputValues: {
      empty: '',
      processing: '',
      result: '',
      'telegram-empty': '',
      'telegram-processing': '',
      'telegram-result': ''
    }
  },

  getters: {
    currentSession(state) {
      return state.sessionItems.find(s => s.key === state.currentSessionKey) || null
    },
    // 当前会话的真实 API 消息（如果是 API 会话）
    // 优先使用预映射缓存，避免每次重新 map 全量原始消息
    currentConversationMessages(state) {
      const item = state.sessionItems.find(s => s.key === state.currentSessionKey)
      if (!item || !item.conversationId) return null
      const convId = item.conversationId
      const cached = state.conversationMappedMessages[convId]
      if (cached && cached.length) return cached
      // 无缓存时降级到 conversationMessages（乐观消息尚未建立缓存的极短窗口期）
      const mapped = state.conversationMessages[convId]
      return mapped && mapped.length ? mapped : null
    },
    currentTelegramPendingReply(state) {
      var key = state.currentTopicKey || state.currentSessionKey || '_default'
      return state.telegramPendingReplies[key] || null
    },
    // 当前会话的 conversationId
    currentConversationId(state) {
      const item = state.sessionItems.find(s => s.key === state.currentSessionKey)
      return item ? (item.conversationId || null) : null
    },
    // 校验过的 currentTopicKey：如果 currentTopicKey 不属于当前会话，返回 null
    safeCurrentTopicKey(state) {
      const item = state.sessionItems.find(s => s.key === state.currentSessionKey)
      const convId = item ? (item.conversationId || null) : null
      return _safeTopicKey(state, convId) || null
    },
    // 当前会话的 typing 状态
    currentAgentTyping(state) {
      const item = state.sessionItems.find(s => s.key === state.currentSessionKey)
      if (!item || !item.conversationId) return null
      const key = _agentKey(item.conversationId, state.currentTopicKey)
      return state.agentTyping[key] || null
    },
    // 当前会话的流式消息
    currentStreamingMessage(state) {
      const item = state.sessionItems.find(s => s.key === state.currentSessionKey)
      if (!item || !item.conversationId) return null
      const key = _agentKey(item.conversationId, state.currentTopicKey)
      return state.agentStreamingMessages[key] || null
    },
    currentTelegramProfile(state) {
      const mock = state.telegramSessionProfiles[state.currentSessionKey]
      if (mock) return mock
      // 没有 mock profile 时，从 session item 构造基本 profile
      const item = state.sessionItems.find(s => s.key === state.currentSessionKey)
      if (!item) return null
      return {
        title: item.name || '',
        sub: '',
        type: item.type,
        avatarImage: item.avatar || null,
        avatarText: (item.name || '?').slice(0, 1),
        avatarColorRgb: item.avatarColorRgb || ''
      }
    },
    filteredSessionItems(state) {
      let items = state.sessionItems.filter(s => !s.exited)
      const query = state.sessionSearchQuery.trim().toLowerCase()
      if (query) {
        items = items.filter(s => s.name.toLowerCase().includes(query))
      }
      if (state.currentSessionFilter !== 'all') {
        items = items.filter(s => s.type === state.currentSessionFilter)
      }
      // 按最新消息时间降序，置顶的在各自分组内保持顶置
      const byTime = (a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0)
      const pinned = items.filter(s => s.pinned).sort(byTime)
      const unpinned = items.filter(s => !s.pinned).sort(byTime)
      return [...pinned, ...unpinned]
    },
    composerSkillPresets: (state) => state.agentSkillList.length ? state.agentSkillList : composerSkillPresets,
    composerModelPresets: (state) => state.modelList.length ? state.modelList : composerModelPresets,
    agentCommandList: (state) => state.commandList,
    currentModelThinks: (state, getters) => {
      const modelId = getters.selectedModel
      const detail = state.modelDetails.find(m => (m.id || m.name) === modelId)
      return detail && Array.isArray(detail.thinks) ? detail.thinks : []
    },
    selectedModel: (state) => {
      // 模型是会话级别的，不区分话题，所有话题共用同一个模型
      const key = state.currentSessionKey || '__global__'
      return state.sessionModelMap[key] || state.modelList[0] || 'FengBaoAuto'
    },
    selectedSkills(state) {
      const key = state.currentSessionKey
        ? (state.currentTopicKey ? `${state.currentSessionKey}:${state.currentTopicKey}` : state.currentSessionKey)
        : '__global__'
      const ids = state.sessionSkillMap[key] || []
      const allSkills = state.agentSkillList.length ? state.agentSkillList : composerSkillPresets
      return allSkills.filter(s => ids.includes(s.id))
    },
    selectedSkillIds(state, getters) {
      const key = state.currentSessionKey
        ? (state.currentTopicKey ? `${state.currentSessionKey}:${state.currentTopicKey}` : state.currentSessionKey)
        : '__global__'
      return state.sessionSkillMap[key] || []
    },
    selectedCommand(state) {
      const key = state.currentSessionKey
        ? (state.currentTopicKey ? `${state.currentSessionKey}:${state.currentTopicKey}` : state.currentSessionKey)
        : '__global__'
      return state.sessionCommandMap[key] || null
    },
    currentAgentProfile(state) {
      return state.agentSessionProfiles[state.currentSessionKey] || null
    },
    agentSoulText(state) {
      const key = state.agentInsightModal.sessionKey || state.currentSessionKey
      if (state.agentSoulOverrides[key] !== undefined) return state.agentSoulOverrides[key]
      // 优先用接口 prompt sections
      const item = state.sessionItems.find(s => s.key === key)
      const convId = item ? item.conversationId : key
      const sections = state.agentPromptSections[convId]
      if (sections && sections.soul) return sections.soul
      return state.agentInspectorProfiles[key]?.soul || '暂无个性设置。'
    },
    agentMemoryText(state) {
      const key = state.agentInsightModal.sessionKey || state.currentSessionKey
      if (state.agentMemoryOverrides[key] !== undefined) return state.agentMemoryOverrides[key]
      const item = state.sessionItems.find(s => s.key === key)
      const convId = item ? item.conversationId : key
      const sections = state.agentPromptSections[convId]
      if (sections && sections.memory) return sections.memory
      return state.agentInspectorProfiles[key]?.memory || '暂无记忆记录。'
    },
    agentPromptSectionsForCurrent(state) {
      const item = state.sessionItems.find(s => s.key === state.currentSessionKey)
      const convId = item ? item.conversationId : state.currentSessionKey
      return state.agentPromptSections[convId] || null
    },
    currentAgentTasks(state) {
      return state.agentTasksData[state.currentSessionKey] || []
    },
    currentGroupPlans(state) {
      const item = state.sessionItems.find(s => s.key === state.currentSessionKey)
      const convId = item ? item.conversationId : null
      const topicId = state.currentTopicKey || null
      if (!convId || !topicId) return []
      const prefix = `${convId}:${topicId}:`
      return Object.keys(state.groupPlans)
        .filter(k => k.startsWith(prefix))
        .map(k => state.groupPlans[k])
    },
    currentTopicsPage(state) {
      return state.sessionTopicPages[state.currentTopicsPageKey] || null
    },
    isWorkspaceVisible(state) {
      return state.workspaceOpen
    },
    appClasses(state) {
      return {
        'workspace-open': state.workspaceOpen || !state.currentSessionKey,
        'workspace-collapsed': !state.workspaceOpen && !!state.currentSessionKey,
        'funcbar-collapsed': state.funcbarCollapsed,
        'funcbar-preview-hover': state.funcbarPreviewHover,
        'workspace-wide': state.workspaceFloat || state.workspaceTakeover,
        'workspace-takeover': state.workspaceTakeover,
        'session-collapsed': state.sessionsPanelCollapsed,
        'expert-center-open': state.currentView === 'experts',
        'team-center-open': state.currentView === 'teams'
      }
    }
  },

  mutations: {
    // ── 会话 & 消息 mutations ────────────────────────────

    // 将 API 会话列表映射为 sessionItems + 话题页，直接用内嵌 topics[]
    SET_API_CONVERSATIONS(state, list) {
      const TOPIC_COLORS = [
        'linear-gradient(135deg,#F06A6A,#F69A9A)',
        'linear-gradient(135deg,#57B2FF,#7AC8FF)',
        'linear-gradient(135deg,#7B61FF,#A28BFF)',
        'linear-gradient(135deg,#F0A83D,#F5C768)',
        'linear-gradient(135deg,#35B58A,#67D3A9)',
        'linear-gradient(135deg,#4F8CF4,#7EB0FF)'
      ]
      const newTopicPages = { ...state.sessionTopicPages }

      const apiSessions = list.map(c => {
        const isGroup = c.type === 'group'
        const isAgent = !isGroup && (c.type === 'agent' || c.is_agent || c.agent_id || c.status === 'agent')
        const sessionType = isGroup ? 'group' : (isAgent ? 'agent' : 'coworker')
        const pageKey = `api-topics-${c.id}`
        const topics = c.topics || []
        const hasMultipleTopics = topics.length > 1

        // 计算 activeTopicId（提到外部保证 lastMessage 块可访问）
        let activeTopicId = null
        if (topics.length) {
          const defaultTopic = topics.find(t => t.isDefault || t.is_default)
          const latestTopic = [...topics]
            .filter(t => t.lastMessageAt || t.last_message_at)
            .sort((a, b) => {
              const ta = a.lastMessageAt || a.last_message_at || ''
              const tb = b.lastMessageAt || b.last_message_at || ''
              return tb.localeCompare(ta)
            })[0]
          activeTopicId = (latestTopic || defaultTopic || topics[0])?.id || null
        }

        // 构建话题页（无额外请求）
        if (topics.length) {
          const mappedTopics = topics.map((t, i) => {
            const lastMsgText = extractLastMessageFromTopic(t)
            const title = t.name || t.title || 'General'
            return {
              key: t.id,
              label: title.length > 6 ? title.slice(0, 6) + '…' : title,
              name: title,
              time: formatRelativeTime(t.lastMessageAt || t.last_message_at || t.createdAt || t.created_at || ''),
              preview: lastMsgText || '暂无最新消息',
              message: lastMsgText || '暂无最新消息',
              unread: t.unread_count || t.unreadCount || '',
              icon: title.slice(0, 1),
              color: TOPIC_COLORS[i % TOPIC_COLORS.length],
              mentionCount: t.mention_count || t.mentionCount || 0,
              isDefault: t.isDefault || t.is_default || false
            }
          })
          newTopicPages[pageKey] = {
            sessionKey: c.id,
            title: c.name || '会话',
            sub: `${mappedTopics.length} 个话题`,
            activeKey: activeTopicId,
            topics: mappedTopics
          }
        }

        // lastMessage 取 activeKey 对应 topic 的最后一条消息，保证列表预览与当前查看的 topic 对应
        let lastMessage = ''
        if (topics.length) {
          // 取 activeKey 对应的 topic
          const activeTopic = topics.find(t => t.id === activeTopicId)
          if (activeTopic) {
            lastMessage = extractLastMessageFromTopic(activeTopic) || ''
          }
          // 如果 active topic 无消息，从其他 topic 降序取
          if (!lastMessage) {
            for (const t of [...topics].sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''))) {
              const text = extractLastMessageFromTopic(t)
              if (text) { lastMessage = text; break }
            }
          }
        }
        if (!lastMessage) lastMessage = extractLastMessage(c)

        return {
          key: c.id,
          conversationId: c.id,
          type: sessionType,
          name: isAgent && c.agentRoleName ? (c.name || '') + '-' + c.agentRoleName : (c.name || '未知会话'),
          pinned: c.pinned || c.is_pinned || c.isPinned || false,
          // 多话题时 hasTopics=true，单话题（General）hasTopics=false
          hasTopics: hasMultipleTopics,
          topicKey: pageKey,
          status: c.status || null,
          metaTime: null,
          date: formatRelativeTime(c.last_message_at || c.lastMessageTime || c.created_at),
          lastMessageAt: new Date(c.last_message_at || c.lastMessageTime || c.created_at || 0).getTime(),
          // 取话题级未读总和、服务端会话级未读、本地已有未读中的最大值
          unread: (() => {
            const serverUnread = c.unread_count || c.unreadCount || 0
            const topicUnread = topics.reduce((sum, t) => sum + (Number(t.unread_count || t.unreadCount) || 0), 0)
            const existing = state.sessionItems.find(s => s.key === c.id)
            const localUnread = existing ? (existing.unread || 0) : 0
            return Math.max(serverUnread, localUnread, topicUnread)
          })(),
          exited: false,
          mentionCount: c.mention_count || c.mentionCount || 0,
          lastMessage,
          avatar: c.avatar || null,
          avatarColorRgb: c.avatarColorRgb || '',
          memberCount: c.memberCount || c.member_count || 0,
          agentCount: c.agentCount || c.agent_count || 0,
          totalCount: c.totalCount || c.total_count || 0,
          hasAgent: c.hasAgent || c.has_agent || false,
          hasMyAgent: c.hasMyAgent || c.has_my_agent || false,
          myAgentStatus: c.myAgentStatus || c.my_agent_status || null,
          isAdmin: c.isAdmin || c.is_admin || false,
          isSystem: !!(c.isSystem || c.is_system),
          peerUserId: c.peerUserId || c.peer_user_id || null,
          peerOnline: null,
          agentId: c.agent_id || c.agentId || null
        }
      })

      state.conversations = list
      state.sessionItems = apiSessions
      state.sessionTopicPages = newTopicPages
      state.conversationsLoaded = true
      if (state.currentSessionKey && !apiSessions.find(s => s.key === state.currentSessionKey)) {
        state.currentSessionKey = null
        state.currentSessionType = 'agent'
      }
    },

    // WS conversation.created — 直接 append 一个新会话到列表
    APPEND_SESSION_FROM_WS(state, c) {
      const TOPIC_COLORS = [
        'linear-gradient(135deg,#F06A6A,#F69A9A)',
        'linear-gradient(135deg,#57B2FF,#7AC8FF)',
        'linear-gradient(135deg,#7B61FF,#A28BFF)'
      ]
      const isGroup = c.type === 'group'
      const isAgent = !isGroup && (c.type === 'agent' || c.is_agent)
      const sessionType = isGroup ? 'group' : (isAgent ? 'agent' : 'coworker')
      const pageKey = `api-topics-${c.id}`
      const topics = c.topics || []
      if (topics.length) {
        const mapped = topics.map((t, i) => {
          const title = t.name || 'General'
          return {
            key: t.id, label: title.length > 6 ? title.slice(0, 6) + '…' : title, name: title,
            time: '', preview: '暂无最新消息', message: '暂无最新消息',
            unread: 0, icon: title.slice(0, 1), color: TOPIC_COLORS[i % TOPIC_COLORS.length],
            mentionCount: 0, isDefault: t.isDefault || t.is_default || false
          }
        })
        Vue.set(state.sessionTopicPages, pageKey, {
          sessionKey: c.id, title: c.name || '会话', sub: mapped.length + ' 个话题',
          activeKey: mapped[0]?.key || null, topics: mapped
        })
      }
      const item = {
        key: c.id, conversationId: c.id, type: sessionType,
        name: isAgent && c.agentRoleName ? (c.name || '') + '-' + c.agentRoleName : (c.name || '新会话'),
        pinned: c.pinned || false, hasTopics: topics.length > 1, topicKey: pageKey,
        status: c.status || null, date: '刚刚', lastMessageAt: Date.now(),
        unread: 0, exited: false, mentionCount: 0, lastMessage: '',
        avatar: c.avatar || null,
        avatarColorRgb: c.avatarColorRgb || '',
        memberCount: c.memberCount || 0, agentCount: c.agentCount || 0, totalCount: c.totalCount || 0,
        hasAgent: c.hasAgent || false, hasMyAgent: c.hasMyAgent || false,
        myAgentStatus: c.myAgentStatus || null, isAdmin: c.isAdmin || false,
        isSystem: !!(c.isSystem || c.is_system),
        peerUserId: c.peerUserId || null, peerOnline: null
      }
      state.sessionItems = [item, ...state.sessionItems]
    },

    // WS topic.created — 直接 append 一个新话题到对应会话
    APPEND_TOPIC_FROM_WS(state, { conversationId, topic }) {
      const TOPIC_COLORS = [
        'linear-gradient(135deg,#F06A6A,#F69A9A)',
        'linear-gradient(135deg,#57B2FF,#7AC8FF)',
        'linear-gradient(135deg,#7B61FF,#A28BFF)'
      ]
      const pageKey = `api-topics-${conversationId}`
      const page = state.sessionTopicPages[pageKey]
      const title = topic.name || topic.title || 'New Topic'
      const newTopic = {
        key: topic.id, label: title.length > 6 ? title.slice(0, 6) + '…' : title, name: title,
        time: '', preview: '暂无最新消息', message: '暂无最新消息',
        unread: 0, icon: title.slice(0, 1), color: TOPIC_COLORS[(page ? page.topics.length : 0) % TOPIC_COLORS.length],
        mentionCount: 0, isDefault: topic.isDefault || topic.is_default || false
      }
      if (page) {
        // 已存在则跳过
        if (page.topics.some(t => t.key === topic.id)) return
        Vue.set(state.sessionTopicPages, pageKey, {
          ...page,
          sub: (page.topics.length + 1) + ' 个话题',
          topics: [...page.topics, newTopic]
        })
      } else {
        Vue.set(state.sessionTopicPages, pageKey, {
          sessionKey: conversationId, title: '', sub: '1 个话题',
          activeKey: topic.id, topics: [newTopic]
        })
      }
      // 更新 sessionItem 的 hasTopics
      const idx = state.sessionItems.findIndex(s => s.conversationId === conversationId)
      if (idx >= 0) {
        const item = state.sessionItems[idx]
        const topicCount = state.sessionTopicPages[pageKey]?.topics?.length || 1
        Vue.set(state.sessionItems, idx, { ...item, hasTopics: topicCount > 1 })
      }
    },

    // WS topic.updated：就地更新话题名/置顶（不全量重拉）。payload 字段 omitempty，只下发本次相关字段
    UPDATE_TOPIC_FROM_WS(state, { conversationId, topicId, name, pinned, status }) {
      if (!conversationId || !topicId) return
      const pageKey = `api-topics-${conversationId}`
      const page = state.sessionTopicPages[pageKey]
      if (!page || !Array.isArray(page.topics)) return
      const ti = page.topics.findIndex(t => t.key === topicId)
      if (ti < 0) return
      const old = page.topics[ti]
      const next = { ...old }
      if (name != null && name !== '') {
        next.name = name
        next.label = name.length > 6 ? name.slice(0, 6) + '…' : name
        next.icon = name.slice(0, 1)
      }
      if (pinned !== undefined) next.pinned = pinned
      if (status != null && status !== '') next.status = status
      const topics = page.topics.slice()
      topics.splice(ti, 1, next)
      Vue.set(state.sessionTopicPages, pageKey, { ...page, topics })
    },

    // 设置某个会话的消息列表（同时保存原始消息用于响应式重映射）
    SET_CONVERSATION_MESSAGES(state, { conversationId, messages, rawMessages }) {
      // 保留还在上传/发送中的 temp 消息（避免切换会话再回来后正在上传的图片/文件被冲掉）
      const existing = state.conversationMessages[conversationId] || []
      const pendingTemps = existing.filter(m =>
        typeof m.id === 'string' && m.id.startsWith('temp-') && m.status === 'sending'
      )
      const merged = pendingTemps.length ? [...messages, ...pendingTemps] : messages
      Vue.set(state.conversationMessages, conversationId, merged)
      if (rawMessages) {
        Vue.set(state.conversationRawMessages, conversationId, rawMessages)
        // 预映射并缓存，避免 getter 每次重算
        const mapped = rawMessages.map(m => mapApiMessageToUi(m, state.currentUserId))
        const mappedMerged = pendingTemps.length ? [...mapped, ...pendingTemps] : mapped
        Vue.set(state.conversationMappedMessages, conversationId, mappedMerged)
      }
    },

    // 追加一条新消息到某个会话
    APPEND_CONVERSATION_MESSAGE(state, { conversationId, message, topicId }) {
      const existing = state.conversationMessages[conversationId] || []
      Vue.set(state.conversationMessages, conversationId, [...existing, message])
      const cachedMapped = state.conversationMappedMessages[conversationId]
      if (cachedMapped) {
        Vue.set(state.conversationMappedMessages, conversationId, [...cachedMapped, message])
      } else {
        Vue.set(state.conversationMappedMessages, conversationId, [...existing, message])
      }
      const newLastMsg = message.text
        || (message.kind === 'image' ? '[图片]' : message.kind === 'audio' ? '[语音]'
          : message.kind === 'video' ? '[视频]'
          : (message.kind === 'file' || message.kind === 'document') ? '[文件]'
          : message.kind === 'multi' ? '[文件]' : '')
      if (newLastMsg) {
        const idx = state.sessionItems.findIndex(s => s.conversationId === conversationId)
        if (idx >= 0) {
          const item = state.sessionItems[idx]
          Vue.set(state.sessionItems, idx, { ...item, lastMessage: newLastMsg, date: '刚刚', lastMessageAt: Date.now() })
          if (topicId && item.topicKey && state.sessionTopicPages[item.topicKey]) {
            const page = state.sessionTopicPages[item.topicKey]
            const topicIdx = page.topics.findIndex(t => t.key === topicId)
            if (topicIdx >= 0) {
              const updatedTopics = page.topics.map((t, i) =>
                i === topicIdx ? { ...t, preview: newLastMsg, message: newLastMsg, time: '刚刚' } : t
              )
              Vue.set(state.sessionTopicPages, item.topicKey, { ...page, topics: updatedTopics })
            }
          }
        }
      }
    },
    REMOVE_CONVERSATION_MESSAGE(state, { conversationId, messageId }) {
      const msgs = state.conversationMessages[conversationId]
      if (!msgs) return
      Vue.set(state.conversationMessages, conversationId, msgs.filter(m => m.id !== messageId))
      const raw = state.conversationRawMessages[conversationId]
      if (raw) Vue.set(state.conversationRawMessages, conversationId, raw.filter(m => m.id !== messageId))
      const mapped = state.conversationMappedMessages[conversationId]
      if (mapped) Vue.set(state.conversationMappedMessages, conversationId, mapped.filter(m => m.id !== messageId))
    },
    // 更新某条消息的状态（发送成功/失败）
    UPDATE_MESSAGE_STATUS(state, { conversationId, messageId, status, progress, ...extra }) {
      const updates = { ...extra }
      if (status !== undefined) updates.status = status
      if (progress !== undefined) updates.progress = progress

      // 同步更新两个存储（conversationMessages 和 conversationMappedMessages）
      const msgs = state.conversationMessages[conversationId]
      if (msgs) {
        const idx = msgs.findIndex(m => m.id === messageId)
        if (idx >= 0) {
          Vue.set(state.conversationMessages, conversationId, msgs.map((m, i) =>
            i === idx ? { ...m, ...updates } : m
          ))
        }
      }
      const mapped = state.conversationMappedMessages[conversationId]
      if (mapped) {
        const idx = mapped.findIndex(m => m.id === messageId)
        if (idx >= 0) {
          Vue.set(state.conversationMappedMessages, conversationId, mapped.map((m, i) =>
            i === idx ? { ...m, ...updates } : m
          ))
        }
      }
    },

    // 设置当前用户 ID（同时重新映射所有已缓存的原始消息，修正消息方向）
    SET_CURRENT_USER_ID(state, userId) {
      state.currentUserId = userId
      // userId 确定后，重新映射所有已缓存原始消息（否则历史消息方向可能错误）
      for (const convId of Object.keys(state.conversationRawMessages)) {
        const raw = state.conversationRawMessages[convId]
        if (raw && raw.length) {
          Vue.set(state.conversationMappedMessages, convId, raw.map(m => mapApiMessageToUi(m, userId)))
        }
      }
    },

    // ── WebSocket mutations ────────────────────────────

    // message.read：更新消息的 readCount
    WS_MESSAGE_READ(state, { conversationId, lastReadSeq, userId, readAt }) {
      // 保险：没有 currentUserId 时不处理（无法判断是否自己）
      if (!state.currentUserId) return
      // 保险：自己读自己的消息不处理（右侧消息都是自己发的）
      if (userId && userId === state.currentUserId) return
      // 用 map 创建新对象引用，确保 Vue 2 响应式检测到变化
      function updateList(messages) {
        if (!messages || !messages.length) return null
        let changed = false
        const updated = messages.map(msg => {
          // 只匹配有真实 seq 且在已读范围内的右侧消息
          // seq=0 的消息无法准确判断，等 fetchConversationMessages 刷新后会有真实 seq
          if (msg.side === 'right' && msg.seq > 0 && lastReadSeq > 0 && msg.seq <= lastReadSeq) {
            changed = true
            return { ...msg, readCount: (msg.readCount || 0) + 1 }
          }
          return msg
        })
        return changed ? updated : null
      }
      const newMsgs = updateList(state.conversationMessages[conversationId])
      if (newMsgs) {
        Vue.set(state.conversationMessages, conversationId, newMsgs)
      }
      const newMapped = updateList(state.conversationMappedMessages[conversationId])
      if (newMapped) {
        Vue.set(state.conversationMappedMessages, conversationId, newMapped)
      }
    },

    // message.new：追加新消息并更新会话预览
    WS_NEW_MESSAGE(state, { conversationId, topicId, message, senderType }) {
      const _rawLenBefore = (state.conversationRawMessages[conversationId] || []).length
      console.log('[WS_NEW_MESSAGE]', { id: message.id, seq: message.seq, side: message.side, text: (message.text || '').slice(0, 30), rawLenBefore: _rawLenBefore })
      // agent 消息到达：处理思考过程结束 + 清掉 streaming
      // message.new 和 reply_end 都是思考结束信号，谁先到谁处理（dedup 在 GroupWorkspacePanel watcher 里）
      if (message.side === 'left' && message.agentTag) {
        // 1. 思考过程结束：取 message.thinking 写入 lastAgentThinking
        if (message.thinking && message.id) {
          state.lastAgentThinking = { id: message.id, thinking: message.thinking, createdAt: new Date().toISOString(), conversationId }
        }
        // 2. 清掉 streaming（content 和 thinking 都到位了）
        const _ak = _agentKey(conversationId, topicId)
        const streaming = state.agentStreamingMessages[_ak]
        if (streaming) {
          Vue.delete(state.agentTyping, _ak)
          Vue.delete(state.agentStreamingMessages, _ak)
        }
      }
      const existing = state.conversationMessages[conversationId] || []
      const dupIdx = existing.findIndex(m => m.id === message.id)
      if (dupIdx >= 0) {
        // id 重복：说明 WS_AGENT_DONE 已写入了一条基于 content 的临时消息（kind 固定为 text）
        // message.new 包含完整的 messageType/files 信息，用它替换掉那条临时消息
        // 不保留旧消息的 html 字段，让 _addHtml 对新消息重新渲染（否则旧 kind:text 的渲染结果残留）
        // _cacheKey 加时间戳后缀，使 LRU 缓存里的旧渲染结果失效
        const { html: _dropNew, ...freshMsg } = message
        freshMsg._cacheKey = message.id + '_r' + Date.now()
        const updated = existing.map((m, i) => {
          if (i !== dupIdx) return m
          const { html: _dropOld, ...base } = m
          return { ...base, ...freshMsg, status: undefined, progress: undefined }
        })
        Vue.set(state.conversationMessages, conversationId, updated)
        const cachedMapped = state.conversationMappedMessages[conversationId]
        if (cachedMapped) {
          const mappedDupIdx = cachedMapped.findIndex(m => m.id === message.id)
          if (mappedDupIdx >= 0) {
            Vue.set(state.conversationMappedMessages, conversationId,
              cachedMapped.map((m, i) => {
                if (i !== mappedDupIdx) return m
                const { html: _dropOld, ...base } = m
                return { ...base, ...freshMsg, status: undefined, progress: undefined }
              })
            )
          } else {
            // mapped 缓存里没有则追加
            Vue.set(state.conversationMappedMessages, conversationId, [...cachedMapped, freshMsg])
          }
        } else {
          // 缓存空则用 conversationMessages 整体初始化
          Vue.set(state.conversationMappedMessages, conversationId, updated)
        }
        // 同步 raw 数组（id 重复时替换，否则追加；打 _isMapped 标记）
        const existingRaw1 = state.conversationRawMessages[conversationId] || []
        // seq 兜底：ws push 时若 seq 缺失或 0，用 Date.now()（毫秒时间戳级别，确保排在末尾）
        const taggedFresh = { ...freshMsg, _isMapped: true, seq: (freshMsg.seq && freshMsg.seq > 0) ? freshMsg.seq : Date.now() }
        const rawDupIdx1 = existingRaw1.findIndex(m => m && m.id === message.id)
        if (rawDupIdx1 >= 0) {
          Vue.set(state.conversationRawMessages, conversationId,
            existingRaw1.map((m, i) => i === rawDupIdx1 ? taggedFresh : m))
        } else {
          Vue.set(state.conversationRawMessages, conversationId, [...existingRaw1, taggedFresh])
        }
        // WS_AGENT_DONE 已不再管理 unread，在这里统一处理
        const sidx = state.sessionItems.findIndex(s => s.conversationId === conversationId)
        if (sidx >= 0) {
          const si = state.sessionItems[sidx]
          const isActive = state.currentSessionKey === si.key
          // 自己发的消息（side === 'right'）不计未读
          const isOwn = message.side === 'right'
          const newUnread = (isActive || isOwn) ? (isActive ? 0 : (si.unread || 0)) : (si.unread || 0) + 1
          const newLastMsg = message.text
            || (message.kind === 'image' ? '[图片]' : message.kind === 'audio' ? '[语音]'
              : message.kind === 'video' ? '[视频]'
              : (message.kind === 'file' || message.kind === 'document') ? '[文件]' : '')
          Vue.set(state.sessionItems, sidx, {
            ...si,
            lastMessage: newLastMsg,
            date: '刚刚',
            lastMessageAt: Date.now(),
            unread: newUnread,
            mentionCount: isActive ? 0 : (si.mentionCount || 0) + (message.isMentioned ? 1 : 0)
          })
          if (!isOwn) {
            state.totalUnread = (state.totalUnread || 0) + 1
          }
        }
        return
      }
      // 自己发的消息（WS 多端同步）：查找 temp 消息替换，防止重复
      if (message.side === 'right') {
        // 精确匹配：文件消息找 temp-file-，文字消息找 temp- 但排除 temp-file-
        const isFileMsg = message.kind && message.kind !== 'text'
        const tempIdx = existing.findIndex(m => typeof m.id === 'string' && m.side === 'right' &&
          (isFileMsg ? m.id.startsWith('temp-file-') : (m.id.startsWith('temp-') && !m.id.startsWith('temp-file-')))
        )
        if (tempIdx >= 0) {
          // 替换 temp 消息为真实消息（丢弃旧 html 缓存，触发重新渲染）
          const updated = existing.map((m, i) => {
            if (i !== tempIdx) return m
            const { html: _drop, ...base } = m
            return { ...base, ...message, status: undefined, progress: undefined }
          })
          Vue.set(state.conversationMessages, conversationId, updated)
          const cachedMapped = state.conversationMappedMessages[conversationId]
          if (cachedMapped) {
            const mappedTempIdx = cachedMapped.findIndex(m => typeof m.id === 'string' && m.side === 'right' &&
              (isFileMsg ? m.id.startsWith('temp-file-') : (m.id.startsWith('temp-') && !m.id.startsWith('temp-file-')))
            )
            if (mappedTempIdx >= 0) {
              Vue.set(state.conversationMappedMessages, conversationId, cachedMapped.map((m, i) => {
                if (i !== mappedTempIdx) return m
                const { html: _drop, ...base } = m
                return { ...base, ...message, status: undefined, progress: undefined }
              }))
            }
          }
          // 同步写 raw 数组（替换 temp 不在 raw，但真实消息要进 raw）
          const existingRaw2 = state.conversationRawMessages[conversationId] || []
          const taggedReal = { ...message, _isMapped: true, seq: (message.seq && message.seq > 0) ? message.seq : Date.now() }
          const rawDupIdx2 = existingRaw2.findIndex(m => m && m.id === message.id)
          if (rawDupIdx2 >= 0) {
            Vue.set(state.conversationRawMessages, conversationId,
              existingRaw2.map((m, i) => i === rawDupIdx2 ? taggedReal : m))
          } else {
            Vue.set(state.conversationRawMessages, conversationId, [...existingRaw2, taggedReal])
          }
          // 更新 sessionItem 但不追加
          const idx = state.sessionItems.findIndex(s => s.conversationId === conversationId)
          if (idx >= 0) {
            const item = state.sessionItems[idx]
            Vue.set(state.sessionItems, idx, { ...item, lastMessage: message.text || item.lastMessage, date: '刚刚', lastMessageAt: Date.now() })
          }
          return
        }
      }
      // 检查消息的 topicId 是否属于当前查看的话题，不匹配则不追加（切换话题后重新拉取会补上）
      const viewingTopicId = state.currentTopicKey || null
      const msgTopicId = topicId || null
      const topicMatch = !(viewingTopicId && msgTopicId && viewingTopicId !== msgTopicId)
      if (topicMatch) {
        // 给 message 打 _isMapped 标记（来自 ws 的已是 mapped 格式，存入 raw 后避免被 mapApiMessageToUi 重新解析）
        const taggedMsg = { ...message, _isMapped: true, seq: (message.seq && message.seq > 0) ? message.seq : Date.now() }
        Vue.set(state.conversationMessages, conversationId, [...existing, taggedMsg])
        // 同步更新 conversationMappedMessages 缓存
        const cachedMapped = state.conversationMappedMessages[conversationId]
        if (cachedMapped) {
          Vue.set(state.conversationMappedMessages, conversationId, [...cachedMapped, taggedMsg])
        } else {
          Vue.set(state.conversationMappedMessages, conversationId, [...existing, taggedMsg])
        }
        // 同步写 raw 数组（消息成为 raw 数组的真实成员，后续合并 by id 去重 + sort 后位置正确）
        const existingRaw = state.conversationRawMessages[conversationId] || []
        const dupRawIdx = existingRaw.findIndex(m => m && m.id === taggedMsg.id)
        if (dupRawIdx >= 0) {
          Vue.set(state.conversationRawMessages, conversationId,
            existingRaw.map((m, i) => i === dupRawIdx ? taggedMsg : m))
        } else {
          Vue.set(state.conversationRawMessages, conversationId, [...existingRaw, taggedMsg])
        }
      }
      const idx = state.sessionItems.findIndex(s => s.conversationId === conversationId)
      if (idx < 0) return
      const item = state.sessionItems[idx]
      const isActive = state.currentSessionKey === item.key
      const isAgentMsg = senderType === 'agent'
      // 自己发的消息（多端同步过来的）不计未读
      const isOwn = message.side === 'right'
      const shouldClearUnread = isActive || isOwn
      // Agent 消息也应该计入未读，只有当前激活会话才清零；自己的消息不增未读
      const newUnread = isActive ? 0 : (isOwn ? (item.unread || 0) : (item.unread || 0) + 1)
      const newLastMsg = message.text
        || (message.kind === 'image' ? '[图片]' : message.kind === 'audio' ? '[语音]' : message.kind === 'video' ? '[视频]' : message.kind === 'file' || message.kind === 'document' ? '[文件]' : '')

      // 更新 sessionTopicPages 里对应 topicId 的 topic，并将 activeKey 切换过去
      if (item.topicKey && state.sessionTopicPages[item.topicKey]) {
        const page = state.sessionTopicPages[item.topicKey]
        const targetTopicKey = topicId || page.activeKey
        const topicIdx = page.topics.findIndex(t => t.key === targetTopicKey)
        if (topicIdx >= 0) {
          // 话题已读条件：当前会话激活 且 当前话题就是该话题；自己的消息也算已读
          const isTopicActive = isActive && state.currentTopicKey === targetTopicKey
          const updatedTopics = page.topics.map((t, i) =>
            i === topicIdx ? {
              ...t,
              preview: newLastMsg,
              message: newLastMsg,
              time: '刚刚',
              unread: isTopicActive ? 0 : (isOwn ? (t.unread || 0) : ((t.unread || 0) + 1)),
              mentionCount: isTopicActive ? 0 : ((t.mentionCount || 0) + (message.isMentioned ? 1 : 0))
            } : t
          )
          Vue.set(state.sessionTopicPages, item.topicKey, {
            ...page,
            activeKey: page.activeKey,
            topics: updatedTopics
          })
          const totalUnread = updatedTopics.reduce((sum, t) => sum + (Number(t.unread) || 0), 0)
          const totalMention = updatedTopics.reduce((sum, t) => sum + (Number(t.mentionCount) || 0), 0)
          Vue.set(state.sessionItems, idx, {
            ...item,
            lastMessage: newLastMsg,
            date: '刚刚',
            lastMessageAt: Date.now(),
            unread: totalUnread,
            mentionCount: totalMention
          })
        } else {
          // 话题不在本地列表中（可能是新话题或未拉取），直接更新 sessionItem
          Vue.set(state.sessionItems, idx, {
            ...item,
            lastMessage: newLastMsg,
            date: '刚刚',
            lastMessageAt: Date.now(),
            unread: newUnread,
            mentionCount: isActive ? 0 : (item.mentionCount || 0) + (message.isMentioned ? 1 : 0)
          })
        }
      } else {
        // ���话题分页时，直��更新 sessionItem
        Vue.set(state.sessionItems, idx, {
          ...item,
          lastMessage: newLastMsg,
          date: '刚刚',
          lastMessageAt: Date.now(),
          unread: newUnread,
          mentionCount: isActive ? 0 : (item.mentionCount || 0) + (message.isMentioned ? 1 : 0)
        })
      }

      if (!isOwn) {
        state.totalUnread = (state.totalUnread || 0) + 1
      }
      if (isActive && item.type === 'agent' && state.mainState === 'empty') {
        state.mainState = 'processing'
      }
    },

    // agent.reply_start：显示 typing 指示器，同时清除上一轮残留的 streaming 内容
    WS_AGENT_TYPING(state, { conversationId, topicId, agentId, agentName, avatarUrl, avatarColorRgb }) {
      const _ak = _agentKey(conversationId, topicId)
      // 清除上一轮可能残留的 streaming（reply_end 丢失的情况）
      if (state.agentStreamingMessages[_ak]) {
        Vue.delete(state.agentStreamingMessages, _ak)
      }
      Vue.set(state.agentTyping, _ak, { agentId, agentName, avatarUrl: avatarUrl || null, avatarColorRgb: avatarColorRgb || '', topicId: topicId || null, startedAt: Date.now() })
    },

    // 清除 agent typing（用于乐观 typing 触发后发送失败的回滚）
    CLEAR_AGENT_TYPING(state, { conversationId, topicId }) {
      const _ak = _agentKey(conversationId, topicId)
      Vue.delete(state.agentTyping, _ak)
    },

    // agent.reply_chunk：流式追加内容（区分思考和正文）
    WS_AGENT_CHUNK(state, { conversationId, content, thinking, isDelta, agentId, agentName, avatarUrl, avatarColorRgb, contentType, topicId }) {
      const _ak = _agentKey(conversationId, topicId)
      const existing = state.agentStreamingMessages[_ak]
      // 取 typing 里的名称和头像作为兜底
      const typing = state.agentTyping[_ak]
      const name = agentName || (existing ? existing.agentName : null) || (typing ? typing.agentName : '') || ''
      const avatar = avatarUrl || (existing ? existing.avatarUrl : null) || (typing ? typing.avatarUrl : null) || null
      const colorRgb = avatarColorRgb || (existing ? existing.avatarColorRgb : '') || (typing ? typing.avatarColorRgb : '') || ''
      // 全量模式（ws.js flush 按 chunkIndex 排序后发送全量内容）
      if (!isDelta) {
        // 全量模式：只更新有值的部分，不覆盖另一个流的累积内容
        const prevContent = existing ? existing.content || '' : ''
        const prevThinking = existing ? existing.thinking || '' : ''
        const newContent = content || prevContent
        const newThinking = thinking || prevThinking
        if (!existing || existing.content !== newContent || existing.thinking !== newThinking) {
          Vue.set(state.agentStreamingMessages, _ak, {
            agentId, agentName: name, avatarUrl: avatar, avatarColorRgb: colorRgb, topicId: topicId || null,
            content: newContent, thinking: newThinking
          })
        }
        return
      }
      // 增量模式（兼容旧逻辑）
      const isThinking = contentType === 'thinking'
      if (!existing) {
        Vue.set(state.agentStreamingMessages, _ak, {
          agentId, agentName: name, avatarUrl: avatar, avatarColorRgb: colorRgb, topicId: topicId || null,
          content: isThinking ? '' : content,
          thinking: isThinking ? content : ''
        })
        return
      }
      if (isThinking) {
        const next = (existing.thinking || '') + (thinking || content)
        if (next !== existing.thinking) {
          Vue.set(state.agentStreamingMessages, _ak, { ...existing, agentId, thinking: next })
        }
      } else {
        const next = (existing.content || '') + content
        if (next !== existing.content) {
          Vue.set(state.agentStreamingMessages, _ak, { ...existing, agentId, content: next })
        }
      }
    },

    // content_end：只处理正文完成，保留思考流式状态
    WS_AGENT_CONTENT_DONE(state, { conversationId, topicId, messageId, content, agentName, agentId, avatarUrl, avatarColorRgb }) {
      const _ak = _agentKey(conversationId, topicId)
      const streaming = state.agentStreamingMessages[_ak]
      const finalContent = (streaming && streaming.content) || content || ''
      // 如果思考还在流式，只清 content；否则全清 streaming
      if (streaming && streaming.thinking) {
        Vue.set(state.agentStreamingMessages, _ak, { ...streaming, content: '' })
      } else {
        Vue.delete(state.agentStreamingMessages, _ak)
      }
      if (!messageId) return
      const existing = state.conversationMessages[conversationId] || []
      if (existing.some(m => m.id === messageId)) return
      const _viewingTopicId = state.currentTopicKey || null
      const _msgTopicId = topicId || null
      const topicMatch = !(_viewingTopicId && _msgTopicId && _viewingTopicId !== _msgTopicId)
      if (topicMatch) {
        const msg = {
          id: messageId,
          side: 'left',
          name: agentName || 'Agent',
          agentTag: 'Agent',
          text: finalContent,
          time: formatTime(new Date().toISOString()),
          avatarText: (agentName || 'A').slice(0, 1),
          avatarImage: avatarUrl || (streaming && streaming.avatarUrl) || null,
          avatarColorRgb: avatarColorRgb || (streaming && streaming.avatarColorRgb) || '',
          kind: 'text',
          seq: 0, readCount: 0, memberCount: 0
        }
        Vue.set(state.conversationMessages, conversationId, [...existing, msg])
        const cachedMapped = state.conversationMappedMessages[conversationId]
        if (cachedMapped) {
          Vue.set(state.conversationMappedMessages, conversationId, [...cachedMapped, msg])
        }
      }
      // 更新 sessionItem 和话题未读
      const sidx = state.sessionItems.findIndex(s => s.conversationId === conversationId)
      if (sidx >= 0) {
        const si = state.sessionItems[sidx]
        const isActive = state.currentSessionKey === si.key
        const lastMsg = finalContent.slice(0, 50) || si.lastMessage
        if (si.topicKey && state.sessionTopicPages[si.topicKey]) {
          const page = state.sessionTopicPages[si.topicKey]
          const targetTopicKey = _msgTopicId || page.activeKey
          const topicIdx = page.topics.findIndex(t => t.key === targetTopicKey)
          if (topicIdx >= 0) {
            const isTopicActive = isActive && _viewingTopicId === targetTopicKey
            const updatedTopics = page.topics.map((t, i) =>
              i === topicIdx ? { ...t, preview: lastMsg, message: lastMsg, time: '刚刚', unread: isTopicActive ? 0 : ((t.unread || 0) + 1) } : t
            )
            Vue.set(state.sessionTopicPages, si.topicKey, { ...page, topics: updatedTopics })
            const totalUnread = updatedTopics.reduce((sum, t) => sum + (Number(t.unread) || 0), 0)
            Vue.set(state.sessionItems, sidx, { ...si, lastMessage: lastMsg, date: '刚刚', lastMessageAt: Date.now(), unread: totalUnread })
          } else {
            Vue.set(state.sessionItems, sidx, { ...si, lastMessage: lastMsg, date: '刚刚', lastMessageAt: Date.now(), unread: isActive ? 0 : (si.unread || 0) + 1 })
          }
        } else {
          Vue.set(state.sessionItems, sidx, { ...si, lastMessage: lastMsg, date: '刚刚', lastMessageAt: Date.now(), unread: isActive ? 0 : (si.unread || 0) + 1 })
        }
      }
    },

    // agent.reply_end：思考过程结束
    // 取 data.thinking 作为最终 thinking 内容
    // 保存到 lastAgentThinking 供右侧工作区追加（dedup 在 watcher 里）
    // 只清 thinking 部分，保留 content（正文可能还在流式）
    // 不再创建聊天消息（消息由 content_end 或 message.new 创建）
    WS_AGENT_DONE(state, { conversationId, topicId, messageId, thinking: replyThinking, agentName, agentId, avatarUrl }) {
      const _ak = _agentKey(conversationId, topicId)
      const streaming = state.agentStreamingMessages[_ak]
      const finalThinking = replyThinking || ''
      if (finalThinking && messageId) {
        state.lastAgentThinking = { id: messageId, thinking: finalThinking, createdAt: new Date().toISOString(), conversationId }
      }
      if (streaming && streaming.content) {
        Vue.set(state.agentStreamingMessages, _ak, { ...streaming, thinking: '' })
      } else {
        Vue.delete(state.agentTyping, _ak)
        Vue.delete(state.agentStreamingMessages, _ak)
      }
    },

    // ── 群聊任务计划（group_plan）──
    SET_GROUP_PLAN(state, { conversationId, topicId, agentId, plan }) {
      const key = `${conversationId}:${topicId}:${agentId}`
      Vue.set(state.groupPlans, key, plan)
    },
    SET_GROUP_PLANS_BATCH(state, { conversationId, topicId, plans }) {
      for (const p of plans) {
        const key = `${conversationId}:${topicId}:${p.agentId}`
        Vue.set(state.groupPlans, key, { agentId: p.agentId, agentName: p.agentName, avatarUrl: p.avatarUrl, metadata: p.metadata })
      }
    },
    CLEAR_GROUP_PLANS(state, { conversationId }) {
      const prefix = conversationId + ':'
      for (const key of Object.keys(state.groupPlans)) {
        if (key.startsWith(prefix)) Vue.delete(state.groupPlans, key)
      }
    },

    SET_AUTH(state, { loggedIn, phone }) {
      state.auth.loggedIn = loggedIn
      if (phone) state.auth.phone = phone
    },
    SET_TEAM_PENDING_COUNT(state, n) {
      state.teamPendingCount = typeof n === 'number' && n >= 0 ? n : 0
    },
    SET_AUTH_USER(state, payload) {
      if (payload.name !== undefined) state.auth.name = payload.name || ''
      if (payload.role !== undefined) state.auth.role = payload.role || ''
      if (payload.positionId !== undefined) state.auth.positionId = payload.positionId || ''
      if (payload.positionName !== undefined) state.auth.positionName = payload.positionName || ''
      if (payload.assistantName !== undefined) state.auth.assistantName = payload.assistantName || ''
      if (payload.avatarUrl !== undefined) state.auth.avatarUrl = payload.avatarUrl || ''
      if (payload.avatarColorRgb !== undefined) state.auth.avatarColorRgb = payload.avatarColorRgb || ''
    },
    LOGOUT(state) {
      state.auth = { loggedIn: false, phone: '', name: '', avatarUrl: '', avatarColorRgb: '', role: '', positionId: '', positionName: '', assistantName: '', setupStep: 0 }
    },
    // 完整登出：清空所有业务数据，防止下一个账号登录时看到上个账号内容
    LOGOUT_RESET(state) {
      state.auth = { loggedIn: false, phone: '', name: '', avatarUrl: '', avatarColorRgb: '', role: '', positionId: '', positionName: '', assistantName: '', setupStep: 0 }
      state.currentUserId = null
      state.conversations = []
      state.sessionItems = []
      state.conversationsLoaded = false
      state.conversationMessages = {}
      state.conversationRawMessages = {}
      state.conversationMappedMessages = {}
      state.conversationFiles = {}
      state.conversationFilesTotal = {}
      state.conversationTopics = {}
      state.conversationAgents = {}
      state.mentionableMembers = {}
      state.availableMembers = {}
      state.teamMembers = []
      state.directContacts = []
      state.pendingMentions = {}
      state.agentTyping = {}
      state.agentStreamingMessages = {}
      state.unreadByConversation = {}
      state.totalUnread = 0
      state.teamPendingCount = 0   // 企业入职审核红点:登出清零,避免跨账号残留
      state.currentSessionKey = null
      state.currentSessionType = 'agent'
      state.sessionTopicPages = {}
      state.currentTopicKey = null
      state.currentTopicsPageKey = null
      state.localReadTopicIds = {}
      state.mainState = 'empty'
      state.workspaceOpen = true
      state.workspaceScene = 'desktop'
    },
    SET_FUNCBAR_COLLAPSED(state, val) {
      state.funcbarCollapsed = val
      setStorageItem('funcbar-collapsed', val ? '1' : '0')
    },
    SET_FUNCBAR_PREVIEW_HOVER(state, val) {
      state.funcbarPreviewHover = val
    },
    SET_WORKSPACE_OPEN(state, val) {
      state.workspaceOpen = val
    },
    SET_CHAT_SEARCH_OPEN(state, val) {
      state.chatSearchOpen = !!val
    },
    SET_WORKSPACE_WIDTH(state, val) {
      state.workspaceWidth = val
    },
    SET_WORKSPACE_SCENE(state, scene) {
      state.workspaceScene = scene
    },
    RESET_CONVERSATIONS_LOADED(state) {
      state.conversationsLoaded = false
    },
    SET_WORKSPACE_FLOAT(state, val) {
      state.workspaceFloat = val
    },
    SET_WORKSPACE_TAKEOVER(state, val) {
      state.workspaceTakeover = val
    },
    SET_GROUP_CLOUD_FLOAT(state, { active, mode }) {
      state.groupCloudFloat = active
      state.groupCloudFloatMode = mode || null
    },
    SET_CURRENT_VIEW(state, view) {
      state.currentView = view
    },
    SET_CURRENT_SESSION(state, { key, type }) {
      state.currentSessionKey = key
      state.currentSessionType = type || 'agent'
      // 切换会话时清空 currentTopicKey、技能和命令列表，避免串会话
      state.currentTopicKey = null
      state.agentSkillList = []
      state.commandList = []
      // 切换会话时退出多选模式
      state.multiSelectMode = false
      state.multiSelectedMessages = []
      // 切换会话时不清除引用回复，切回来时保留
    },
    SET_SESSION_FILTER(state, filter) {
      state.currentSessionFilter = filter
    },
    SET_SESSION_SEARCH(state, query) {
      state.sessionSearchQuery = query
    },
    SET_SESSIONS_PANEL_COLLAPSED(state, val) {
      state.sessionsPanelCollapsed = val
    },
    SET_TOPICS_VIEW(state, { open, pageKey }) {
      state.topicsViewOpen = open
      if (pageKey) {
        state.currentTopicsPageKey = pageKey
        const page = state.sessionTopicPages[pageKey]
        if (page) {
          const defaultKey = page.activeKey || (page.topics[0] && page.topics[0].key) || null
          state.currentTopicKey = defaultKey
          if (defaultKey) page.activeKey = defaultKey
        }
      }
    },
    SET_CURRENT_TOPIC_KEY(state, key) {
      state.currentTopicKey = key
    },
    SET_TOPIC_PAGE_ACTIVE_KEY(state, { pageKey, topicKey }) {
      const page = state.sessionTopicPages[pageKey]
      if (page) Vue.set(state.sessionTopicPages, pageKey, { ...page, activeKey: topicKey })
    },
    SET_SESSION_TOPIC_PAGE(state, { pageKey, page }) {
      Vue.set(state.sessionTopicPages, pageKey, page)
    },
    SET_ONLINE_MEMBERS_REFRESH(state, ts) {
      state.onlineMembersRefreshAt = ts
    },
    SET_MAIN_STATE(state, mainState) {
      state.mainState = mainState
    },
    INCREMENT_ONLINE_COUNT_VERSION(state) {
      state.onlineCountVersion = (state.onlineCountVersion || 0) + 1
    },
    SET_ONLINE_COUNT(state, { conversationId, count }) {
      Vue.set(state.conversationOnlineCounts, conversationId, count)
    },
    SET_FAKE_FLOW_RUNNING(state, val) {
      state.fakeFlowRunning = val
    },
    SET_TELEGRAM_PROMPT(state, prompt) {
      state.currentTelegramPrompt = prompt
    },
    SET_TELEGRAM_PENDING_REPLY(state, reply) {
      var key = state.currentTopicKey || state.currentSessionKey || '_default'
      if (reply) Vue.set(state.telegramPendingReplies, key, reply)
      else Vue.delete(state.telegramPendingReplies, key)
    },
    SET_TELEGRAM_SENT_REPLY(state, reply) {
      state.currentTelegramSentReply = reply
    },
    SET_CONVERSATION_AGENTS(state, { conversationId, agents }) {
      state.conversationAgents = { ...state.conversationAgents, [conversationId]: agents }
    },
    // ── 置顶消息 mutations ──
    SET_PINNED_MESSAGES(state, { key, items }) {
      Vue.set(state.conversationPinnedMessages, key, Array.isArray(items) ? items : [])
    },
    ADD_PINNED_MESSAGE(state, { key, item }) {
      if (!item || !item.messageId) return
      const list = state.conversationPinnedMessages[key] || []
      // 用 messageId 去重（乐观更新后 WS 广播回声会重复）
      const filtered = list.filter(x => x.messageId !== item.messageId)
      Vue.set(state.conversationPinnedMessages, key, [item, ...filtered])
    },
    REMOVE_PINNED_MESSAGE(state, { key, messageId }) {
      if (!messageId) return
      const list = state.conversationPinnedMessages[key] || []
      Vue.set(state.conversationPinnedMessages, key, list.filter(x => x.messageId !== messageId))
    },
    REMOVE_PINNED_MESSAGE_ANY(state, messageId) {
      // 删除消息时全局清理（不知道是哪个 topic，遍历）
      if (!messageId) return
      Object.keys(state.conversationPinnedMessages).forEach(key => {
        const list = state.conversationPinnedMessages[key]
        if (list && list.some(x => x.messageId === messageId)) {
          Vue.set(state.conversationPinnedMessages, key, list.filter(x => x.messageId !== messageId))
        }
      })
    },
    SET_PINNED_BAR_DISMISSED(state, { key, dismissed }) {
      if (dismissed) Vue.set(state.pinnedBarDismissed, key, true)
      else Vue.delete(state.pinnedBarDismissed, key)
    },
    SET_PINNED_PANEL_OPEN(state, open) {
      state.pinnedPanelOpen = !!open
    },
    SET_WECHAT_BINDING(state, { agentId, binding }) {
      Vue.set(state.wechatBindings, agentId, binding)
    },
    SET_CONVERSATION_FILES(state, { conversationId, files, total, append }) {
      if (append) {
        const prev = state.conversationFiles[conversationId] || []
        state.conversationFiles = { ...state.conversationFiles, [conversationId]: prev.concat(files) }
      } else {
        state.conversationFiles = { ...state.conversationFiles, [conversationId]: files }
      }
      state.conversationFilesTotal = { ...state.conversationFilesTotal, [conversationId]: total || files.length }
    },
    SET_TOPIC_FOLDER_TREE(state, { topicId, data }) {
      Vue.set(state.topicFolderTree, topicId, data)
    },
    SET_CONVERSATION_FILES_LOADING(state, loading) {
      state.conversationFilesLoading = loading
    },
    SET_CONVERSATION_ARTIFACT_FILES(state, { conversationId, files }) {
      state.conversationArtifactFiles = { ...state.conversationArtifactFiles, [conversationId]: files }
    },
    SET_FILE_VERSIONS(state, { fileId, data }) {
      state.fileVersionsData = { ...state.fileVersionsData, [fileId]: data }
    },
    SET_FILE_VERSIONS_LOADING(state, loading) {
      state.fileVersionsLoading = loading
    },
    SET_TOTAL_UNREAD(state, { total, byConversation }) {
      state.totalUnread = total || 0
      if (byConversation && Array.isArray(byConversation)) {
        const map = {}
        byConversation.forEach(item => {
          if (item.conversationId) map[item.conversationId] = item.unreadCount || item.count || 0
        })
        state.unreadByConversation = map
        // 同步 mentionCount 到 sessionItems
        byConversation.forEach(item => {
          if (!item.conversationId) return
          const idx = state.sessionItems.findIndex(s => s.conversationId === item.conversationId)
          if (idx >= 0) {
            Vue.set(state.sessionItems, idx, {
              ...state.sessionItems[idx],
              mentionCount: item.mentionCount || 0
            })
          }
        })
      }
    },
    SET_MENTIONABLE_MEMBERS(state, { conversationId, members }) {
      state.mentionableMembers = { ...state.mentionableMembers, [conversationId]: members }
    },
    SET_AVAILABLE_MEMBERS(state, { conversationId, members }) {
      state.availableMembers = { ...state.availableMembers, [conversationId]: members }
    },
    SET_TEAM_MEMBERS(state, members) {
      state.teamMembers = members
    },
    SET_DIRECT_CONTACTS(state, members) {
      state.directContacts = members
    },
    SET_CONVERSATION_TOPICS(state, { conversationId, topics }) {
      state.conversationTopics = { ...state.conversationTopics, [conversationId]: topics }
    },
    ADD_PENDING_MENTION(state, { name, mention }) {
      state.pendingMentions = { ...state.pendingMentions, [name]: mention }
    },
    CLEAR_PENDING_MENTIONS(state) {
      state.pendingMentions = {}
    },

    SET_GROUP_WORKSPACE_TAB(state, tab) {
      state.currentGroupWorkspaceTab = tab
    },
    SET_GROUP_WORKSPACE_SESSION(state, { sessionKey, topicKey }) {
      state.currentGroupWorkspaceSessionKey = sessionKey
      state.currentGroupWorkspaceTopicKey = topicKey || null
    },
    SET_GROUP_WORKSPACE_SELECTION(state, { tab, id }) {
      Vue.set(state.currentGroupWorkspaceSelections, tab, id)
    },
    OPEN_IFRAME_MODAL(state, { url, title }) {
      state.iframeModal = { open: true, url: url || '', title: title || '' }
    },
    CLOSE_IFRAME_MODAL(state) {
      state.iframeModal = { open: false, url: '', title: '' }
    },
    SET_FULLSCREEN_PREVIEW(state, item) {
      state.fullscreenPreviewItem = item
    },
    // ── 图片画廊（左右切换）──────────────────────────────
    SET_FULLSCREEN_GALLERY(state, payload) {
      state.fullscreenGallery = payload
    },
    PREPEND_GALLERY(state, { items, hasOlder }) {
      const g = state.fullscreenGallery
      if (!g) return
      g.loaded = items.concat(g.loaded)
      g.baseIndex -= items.length   // 头部插入 → 已加载窗口的起点绝对下标前移
      g.hasOlder = hasOlder
    },
    APPEND_GALLERY(state, { items, hasNewer }) {
      const g = state.fullscreenGallery
      if (!g) return
      g.loaded = g.loaded.concat(items)
      g.hasNewer = hasNewer
    },
    SET_GALLERY_CUR(state, absIndex) {
      if (state.fullscreenGallery) state.fullscreenGallery.cur = absIndex
    },
    SET_GALLERY_LOADING(state, v) {
      if (state.fullscreenGallery) state.fullscreenGallery.loading = !!v
    },
    CLEAR_FULLSCREEN_GALLERY(state) {
      state.fullscreenGallery = null
    },
    SET_GROUP_WORKSPACE_PREVIEW(state, item) {
      // 如果没有 kind，根据 ext 自动推断
      if (item && !item.kind && item.ext) {
        item.kind = guessKindByExt('.' + item.ext) || item.kind
      }
      state.currentGroupWorkspacePreview = item
    },
    SET_GROUP_WORKSPACE_ITEMS(state, items) {
      state.groupWorkspaceItems = items
    },
    SET_AGENT_INSIGHT_MODAL(state, payload) {
      Object.assign(state.agentInsightModal, payload)
    },
    SET_AGENT_DETAIL_POPOVER(state, payload) {
      Object.assign(state.agentDetailPopover, payload)
    },
    SET_AGENT_TASK_BUBBLE(state, payload) {
      Object.assign(state.agentTaskBubble, payload)
    },
    SET_AGENT_TASK_PREVIEW(state, payload) {
      Object.assign(state.agentTaskPreview, payload)
    },
    SET_CONTEXT_USAGE_DISPLAY(state, { conversationId, value }) {
      Vue.set(state.contextUsageDisplay, conversationId, value)
    },
    SET_AGENT_SCHEDULED_TASKS(state, { conversationId, tasks }) {
      Vue.set(state.agentScheduledTasks, conversationId, tasks)
    },
    SET_AGENT_SCHEDULED_TASKS_LOADING(state, loading) {
      state.agentScheduledTasksLoading = loading
    },
    SET_AGENT_PROMPT_SECTIONS(state, { conversationId, sections }) {
      Vue.set(state.agentPromptSections, conversationId, sections)
    },
    SET_AGENT_PROMPT_SECTIONS_LOADING(state, loading) {
      state.agentPromptSectionsLoading = loading
    },
    SET_AGENT_SOUL_OVERRIDE(state, { key, value }) {
      Vue.set(state.agentSoulOverrides, key, value)
    },
    DELETE_AGENT_SOUL_OVERRIDE(state, key) {
      Vue.delete(state.agentSoulOverrides, key)
    },
    SET_AGENT_MEMORY_OVERRIDE(state, { key, value }) {
      Vue.set(state.agentMemoryOverrides, key, value)
    },
    DELETE_AGENT_MEMORY_OVERRIDE(state, key) {
      Vue.delete(state.agentMemoryOverrides, key)
    },
    SET_TELEGRAM_GROUP_MENU(state, payload) { state.telegramGroupMenu = payload },
    SET_TELEGRAM_REPLY_MENU(state, payload) { state.telegramReplyMenu = payload },
    SET_FORWARD_MODAL(state, payload) { state.forwardModal = payload },
    CLOSE_FORWARD_MODAL(state) { state.forwardModal = { open: false, senderName: '', text: '', kind: 'text', imageUrl: '', fileName: '', files: null } },
    ENTER_MULTI_SELECT(state, msg) {
      state.multiSelectMode = true
      state.multiSelectedMessages = msg ? [msg] : []
    },
    TOGGLE_MULTI_SELECT_MSG(state, msg) {
      const idx = state.multiSelectedMessages.findIndex(m => m.id === msg.id)
      if (idx >= 0) state.multiSelectedMessages.splice(idx, 1)
      else state.multiSelectedMessages.push(msg)
    },
    EXIT_MULTI_SELECT(state) {
      state.multiSelectMode = false
      state.multiSelectedMessages = []
    },
    SET_TELEGRAM_EMOJI_MENU(state, payload) { state.telegramEmojiMenu = payload },
    SET_TELEGRAM_MENTION_MENU(state, payload) {
      const wasOpen = state.telegramMentionMenu.open
      const oldMembers = state.telegramMentionMenu.members
      state.telegramMentionMenu = { ...payload }
      // 只在菜单从关闭变为打开，或成员列表变化时重置索引
      if (!wasOpen && payload.open) {
        state.mentionActiveIndex = 0
      } else if (payload.members && oldMembers && payload.members.length !== oldMembers.length) {
        state.mentionActiveIndex = 0
      }
    },
    SET_MENTION_ACTIVE_INDEX(state, idx) { state.mentionActiveIndex = idx },
    CLOSE_TELEGRAM_MENUS(state) {
      state.telegramGroupMenu = { ...state.telegramGroupMenu, open: false }
      state.telegramReplyMenu = { ...state.telegramReplyMenu, open: false }
      state.telegramEmojiMenu = { ...state.telegramEmojiMenu, open: false }
      state.telegramMentionMenu = { ...state.telegramMentionMenu, open: false }
    },
    SET_GROUP_DIALOG(state, { open, mode, sessionKey, data }) {
      state.groupDialog = { open: !!open, mode: mode || null, sessionKey: sessionKey || '' }
    },

    SET_CLOUD_ACTION_BUSY(state, msg) { state.cloudActionBusy = msg || '' },
    SET_VNC_MODAL(state, payload) {
      state.vncModal = payload
        ? { open: true, url: payload.url || '', agentName: payload.agentName || '', takeover: !!payload.takeover }
        : { open: false, url: '', agentName: '', takeover: false }
    },
    INCREMENT_CREATED_GROUP_COUNT(state) {
      state.createdGroupCount++
    },
    ADD_SESSION_ITEM(state, item) {
      state.sessionItems.push(item)
    },
    UPDATE_SESSION_ITEM(state, { key, updates }) {
      const idx = state.sessionItems.findIndex(s => s.key === key)
      if (idx >= 0) {
        const old = state.sessionItems[idx]
        Vue.set(state.sessionItems, idx, { ...old, ...updates })
      }
    },
    // WS agent.disabled：记录最近一次停用事件，WorkspaceView watch 这个 tick 来局部更新 agentItems / 关闭详情
    WS_AGENT_DISABLED(state, { agentId, agentName, ownerId, ownerName, disabledAt }) {
      state.lastAgentDisabled = { agentId, agentName: agentName || '', ownerId: ownerId || '', ownerName: ownerName || '', disabledAt: disabledAt || '', ts: Date.now() }
    },
    SET_SELECTED_SKILLS(state, ids) {
      // legacy - not used
      state.selectedSkillIds = ids
    },
    TOGGLE_SKILL(state, skillId) {
      const key = state.currentSessionKey
        ? (state.currentTopicKey ? `${state.currentSessionKey}:${state.currentTopicKey}` : state.currentSessionKey)
        : '__global__'
      const ids = state.sessionSkillMap[key] || []
      const idx = ids.indexOf(skillId)
      const adding = idx < 0
      Vue.set(state.sessionSkillMap, key, adding
        ? [...ids, skillId]
        : ids.filter(id => id !== skillId)
      )
      // 选技能时清掉命令
      if (adding) Vue.set(state.sessionCommandMap, key, null)
    },
    CLEAR_SKILLS(state, skillId) {
      const key = state.currentSessionKey
        ? (state.currentTopicKey ? `${state.currentSessionKey}:${state.currentTopicKey}` : state.currentSessionKey)
        : '__global__'
      const ids = state.sessionSkillMap[key] || []
      Vue.set(state.sessionSkillMap, key, skillId ? ids.filter(id => id !== skillId) : [])
    },
    SET_SESSION_COMMAND(state, cmd) {
      const key = state.currentSessionKey
        ? (state.currentTopicKey ? `${state.currentSessionKey}:${state.currentTopicKey}` : state.currentSessionKey)
        : '__global__'
      Vue.set(state.sessionCommandMap, key, cmd)
    },
    SET_SELECTED_MODEL(state, model) {
      // 模型是会话级别的，不区分话题
      const key = state.currentSessionKey || '__global__'
      Vue.set(state.sessionModelMap, key, model)
      state.selectedModel = model
      // 切换模型时同步思考深度为该模型的 defaultThink
      const detail = state.modelDetails.find(m => (m.id || m.name) === model)
      if (detail && detail.defaultThink) {
        state.thinkingDepth = detail.defaultThink
      }
    },
    SET_SKILL_PICKER_OPEN(state, payload) {
      if (typeof payload === 'boolean') {
        state.skillPickerOpen = payload
      } else {
        state.skillPickerOpen = payload.open
        if (payload.rect) {
          const r = payload.rect
          state.skillPickerTriggerRect = { top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height }
        }
      }
    },
    SET_MODEL_LIST(state, list) {
      state.modelList = list
      // 加载后默认选中第一个
      if (list.length) {
        state.selectedModel = list[0]
      }
    },
    SET_MODEL_DETAILS(state, details) {
      state.modelDetails = details
    },
    SET_COMMAND_LIST(state, list) {
      state.commandList = list
    },
    SET_COMMAND_LIST_LOADING(state, val) {
      state.commandListLoading = val
    },
    SET_AGENT_SKILL_LIST(state, list) {
      state.agentSkillList = list
    },
    SET_AGENT_SKILL_LOADING(state, val) {
      state.agentSkillLoading = val
    },
    SET_NOTIFICATION_SETTINGS(state, settings) {
      state.notificationSettings = { ...state.notificationSettings, ...settings }
    },
    SET_HOTKEY_SETTINGS(state, settings) {
      state.hotkeySettings = settings
    },
    SET_MODEL_PICKER_OPEN(state, payload) {
      if (typeof payload === 'boolean') {
        state.modelPickerOpen = payload
      } else {
        state.modelPickerOpen = payload.open
        if (payload.rect) {
          const r = payload.rect
          state.modelPickerTriggerRect = { top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height }
        }
      }
    },
    SET_THINKING_DEPTH(state, depth) {
      state.thinkingDepth = depth
    },
    SET_THINKING_DEPTH_PICKER_OPEN(state, payload) {
      if (typeof payload === 'boolean') {
        state.thinkingDepthPickerOpen = payload
      } else {
        state.thinkingDepthPickerOpen = payload.open
        if (payload.rect) {
          const r = payload.rect
          state.thinkingDepthPickerRect = { top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height }
        }
      }
    },
    SET_SESSION_CONTEXT_MENU(state, payload) {
      Object.assign(state.sessionContextMenu, payload)
    },
    SET_SESSION_ADD_MENU_OPEN(state, val) {
      state.sessionAddMenuOpen = val
    },
    SET_PROCESSING_CONTENT(state, content) {
      Object.assign(state.processingContent, content)
    },
    SET_WORKSPACE_RESULT_DATA(state, data) {
      Object.assign(state.workspaceResultData, data)
    },
    SET_WORKSPACE_FILE(state, file) {
      state.workspaceFile = file
      if (file) {
        state.workspaceScene = 'file'
        state.workspaceOpen = true
      }
    },
    SET_WORKSPACE_BROWSER(state, { address, title, desc }) {
      if (address !== undefined) state.workspaceBrowserAddress = address
      if (title !== undefined) state.workspaceBrowserTitle = title
      if (desc !== undefined) state.workspaceBrowserDesc = desc
    },
    SET_COMPOSER_INPUT(state, { context, value }) {
      Vue.set(state.composerInputValues, context, value)
    },
    TOGGLE_SESSION_PIN(state, key) {
      const item = state.sessionItems.find(s => s.key === key)
      if (item) Vue.set(item, 'pinned', !item.pinned)
    },
    ADD_TELEGRAM_PROFILE(state, { key, profile }) {
      Vue.set(state.telegramSessionProfiles, key, profile)
    }
  },

  actions: {
    async initApp({ commit, dispatch }) {
      const collapsed = getStorageItem('funcbar-collapsed')
      if (collapsed === '1') commit('SET_FUNCBAR_COLLAPSED', true)
      commit('SET_WORKSPACE_OPEN', true)
      commit('SET_WORKSPACE_SCENE', 'desktop')
      commit('SET_MAIN_STATE', 'empty')
      // 每次启动都拉一次 /users/me，确保 name/avatarUrl 最新
      dispatch('fetchUserProfile')
    },

    // 单独拉取 /users/me，不受 currentUserId 缓存影响
    async fetchUserProfile({ commit }) {
      try {
        const me = await api.getMe()
        if (!me) return
        // 响应结构：{ id, name, username, avatarUrl, role, status, team_id, ... }
        const userId = me.id
        if (userId) commit('SET_CURRENT_USER_ID', userId)
        commit('SET_AUTH_USER', {
          name: me.name || me.username || '',
          avatarUrl: me.avatarUrl || '',
          avatarColorRgb: me.avatarColorRgb || '',
          positionId: me.positionId || '',
          positionName: me.positionName || ''
        })
        try {
          const s = getAuthState()
          if (s) localStorage.setItem('mcn_auth', JSON.stringify({
            ...s,
            ...(userId ? { userId } : {}),
            name: me.name || me.username || s.name || '',
            avatarUrl: me.avatarUrl || '',
            positionId: me.positionId || '',
            positionName: me.positionName || ''
          }))
        } catch (e) { console.warn('[store] persist auth failed:', e.message) }
      } catch (e) { console.warn('[store] fetchUserProfile failed:', e.message) }
    },

    // ── API Actions ──────────────────────────────────────

    // 获取当前用户 ID，三级回退策略：localStorage → JWT → GET /users/me
    async fetchCurrentUser({ commit, state }) {
      if (state.currentUserId) return  // 已有则直接返回

      // 1. 从 localStorage 读取（登录时写入）
      try {
        const s = getAuthState()
        if (s && s.userId) {
          commit('SET_CURRENT_USER_ID', s.userId)
          return
        }
      } catch (e) { console.warn('[store] read auth state failed:', e.message) }

      // 2. 解码 JWT token
      const jwtId = getUserIdFromJwt()
      if (jwtId) {
        commit('SET_CURRENT_USER_ID', jwtId)
        // 持久化，下次无需再解码
        try {
          const s = getAuthState()
          if (s) localStorage.setItem('mcn_auth', JSON.stringify({ ...s, userId: jwtId }))
        } catch (e) { console.warn('[store] persist jwt userId failed:', e.message) }
        return
      }

      // 3. 调用 GET /users/me（网络请求）
      try {
        const me = await api.getMe()
        const userId = me && me.id
        if (userId) {
          commit('SET_CURRENT_USER_ID', userId)
          commit('SET_AUTH_USER', {
            name: me.name || me.username || '',
            avatarUrl: me.avatarUrl || '',
            positionId: me.positionId || '',
            positionName: me.positionName || ''
          })
          try {
            const s = getAuthState()
            if (s) localStorage.setItem('mcn_auth', JSON.stringify({
              ...s, userId,
              name: me.name || me.username || s.name || '',
              avatarUrl: me.avatarUrl || '',
              positionId: me.positionId || '',
              positionName: me.positionName || ''
            }))
          } catch (e) { console.warn('[store] persist user info failed:', e.message) }
        }
      } catch (e) { console.warn('[store] fetchCurrentUser failed:', e.message) }
    },

    // GET /settings/notifications - 拉取通知设置缓存到 store
    async fetchNotificationSettings({ commit }) {
      try {
        const data = await api.getNotificationSettings()
        if (data) commit('SET_NOTIFICATION_SETTINGS', data)
      } catch (e) {
        console.warn('[store] fetchNotificationSettings failed:', e.message)
      }
    },

    // GET /settings/general - 加载快捷键设置
    async fetchHotkeySettings({ commit }) {
      try {
        const data = await api.getGeneralSettings()
        if (data) {
          const parse = (str) => {
            if (!str) return { key: '' }
            const parts = str.toLowerCase().split('+').map(s => s.trim())
            const hk = { key: '' }
            for (const p of parts) {
              if (p === 'ctrl') hk.ctrl = true
              else if (p === 'shift') hk.shift = true
              else if (p === 'alt') hk.alt = true
              else hk.key = p === 'enter' ? 'Enter' : p === 'space' ? ' ' : p
            }
            return hk
          }
          const settings = { send: parse(data.sendKey || 'enter'), newline: parse(data.newlineKey || 'ctrl+enter') }
          commit('SET_HOTKEY_SETTINGS', settings)
          localStorage.setItem('arp_hotkey_settings', JSON.stringify(settings))
        }
      } catch (e) { console.warn('[store] fetchHotkeySettings failed:', e.message) }
    },

    // GET /agents/capabilities?agent_id=xxx - 加载命令列表（切换会话时调用）
    async fetchAgentCapabilities({ commit, state }, conversationId) {
      if (!conversationId) return
      const agents = state.conversationAgents[conversationId] || []
      let agentId = agents.length ? agents[0].id : null
      if (!agentId) {
        const session = state.sessionItems.find(s => s.conversationId === conversationId)
        agentId = session && session.agentId ? session.agentId : null
      }
      if (!agentId) return
      commit('SET_COMMAND_LIST_LOADING', true)
      try {
        const data = await api.getAgentCapabilities(agentId)
        const total = data && Array.isArray(data.total) ? data.total : []
        commit('SET_COMMAND_LIST', total)
      } catch (e) {
        console.warn('[store] fetchAgentCapabilities failed:', e.message)
      } finally {
        commit('SET_COMMAND_LIST_LOADING', false)
      }
    },
    // GET /agents/capabilities?agent_id=xxx - 加载技能列表（点击技能按钮时调用）
    async fetchAgentSkills({ commit, state }, conversationId) {
      if (!conversationId) return
      const agents = state.conversationAgents[conversationId] || []
      let agentId = agents.length ? agents[0].id : null
      if (!agentId) {
        const session = state.sessionItems.find(s => s.conversationId === conversationId)
        agentId = session && session.agentId ? session.agentId : null
      }
      if (!agentId) return
      commit('SET_AGENT_SKILL_LOADING', true)
      try {
        const data = await api.getAgentCapabilities(agentId)
        const skills = data && Array.isArray(data.skills) ? data.skills : []
        const mappedSkills = skills.map(s => ({
          id: s.skillId || s.name,
          icon: s.skillIcon || '🔧',
          name: s.name || s.skillName || '',
          skillDescription: s.skillDescription || '',
          command: s.command || '',
          skillName: s.skillName || s.name
        }))
        commit('SET_AGENT_SKILL_LIST', mappedSkills)
      } catch (e) {
        console.warn('[store] fetchAgentSkills failed:', e.message)
      } finally {
        commit('SET_AGENT_SKILL_LOADING', false)
      }
    },
    // 合并：一次请求同时拿命令和技能
    async fetchAgentCapabilitiesAndSkills({ commit, state }, conversationId) {
      if (!conversationId) return
      const agents = state.conversationAgents[conversationId] || []
      let agentId = agents.length ? agents[0].id : null
      if (!agentId) {
        const session = state.sessionItems.find(s => s.conversationId === conversationId)
        agentId = session && session.agentId ? session.agentId : null
      }
      if (!agentId) return
      // 记录发起请求时的会话 key，用于检查是否已切换
      const requestSessionKey = state.currentSessionKey
      commit('SET_COMMAND_LIST_LOADING', true)
      commit('SET_AGENT_SKILL_LOADING', true)
      try {
        const data = await api.getAgentCapabilities(agentId)
        // 请求返回后检查会话是否已切换，切换则丢弃结果
        if (state.currentSessionKey !== requestSessionKey) return
        const total = data && Array.isArray(data.total) ? data.total : []
        commit('SET_COMMAND_LIST', total)
        const skills = data && Array.isArray(data.skills) ? data.skills : []
        commit('SET_AGENT_SKILL_LIST', skills.map(s => ({
          id: s.skillId || s.name,
          icon: s.skillIcon || '🔧',
          name: s.name || s.skillName || '',
          skillDescription: s.skillDescription || '',
          command: s.command || '',
          skillName: s.skillName || s.name
        })))
      } catch (e) {
        console.warn('[store] fetchAgentCapabilitiesAndSkills failed:', e.message)
      } finally {
        if (state.currentSessionKey === requestSessionKey) {
          commit('SET_COMMAND_LIST_LOADING', false)
          commit('SET_AGENT_SKILL_LOADING', false)
        }
      }
    },


    // GET /conversations/:id/topics/:topicId/group-plans
    async fetchGroupPlans({ commit }, { conversationId, topicId }) {
      if (!conversationId || !topicId) return
      try {
        const data = await api.getGroupPlans(conversationId, topicId)
        const plans = data && data.plans ? data.plans : (data && data.data && data.data.plans ? data.data.plans : [])
        commit('SET_GROUP_PLANS_BATCH', { conversationId, topicId, plans })
      } catch (e) {
        console.warn('[store] fetchGroupPlans failed:', e.message)
      }
    },

    // GET /agents/:id/prompt-sections - 加载 Agent 的 prompt sections
    async fetchAgentPromptSections({ commit, state }, conversationId) {
      if (!conversationId) return
      const agents = state.conversationAgents[conversationId] || []
      const agentId = agents.length ? agents[0].id : null
      if (!agentId) return
      commit('SET_AGENT_PROMPT_SECTIONS_LOADING', true)
      try {
        const raw = await api.getAgentPromptSections(agentId)
        // raw = json.data = { ok, agent_id, sections, system_prompt }
        const sections = (raw && raw.sections) ? raw.sections : {}
        commit('SET_AGENT_PROMPT_SECTIONS', { conversationId, sections })
      } catch (e) {
        console.warn('[store] fetchAgentPromptSections failed:', e.message)
      } finally {
        commit('SET_AGENT_PROMPT_SECTIONS_LOADING', false)
      }
    },

    // PATCH /agents/:id/prompt-sections - 保存 prompt section
    async updateAgentPromptSection({ commit, state }, { conversationId, sectionKey, value }) {
      if (!conversationId || !sectionKey) return
      const agents = state.conversationAgents[conversationId] || []
      const agentId = agents.length ? agents[0].id : null
      if (!agentId) return
      // 取当前缓存的完整 sections，替换要改的那项，整体传回去
      const cached = state.agentPromptSections[conversationId] || {}
      const payload = {
        identity: cached.identity || '',
        soul: cached.soul || '',
        persona: cached.persona || '',
        style: cached.style || '',
        system: cached.system || '',
        memory: cached.memory || '',
        [sectionKey]: value
      }
      try {
        const data = await api.updateAgentPromptSections(agentId, payload)
        const sections = data && data.sections ? data.sections : null
        if (sections) {
          commit('SET_AGENT_PROMPT_SECTIONS', { conversationId, sections })
        }
      } catch (e) {
        console.warn('[store] updateAgentPromptSection failed:', e.message)
      }
    },

    // POST /agents/:id/prompt-sections/reset - 恢复 prompt section 为角色默认值
    async resetAgentPromptSection({ commit, state }, { conversationId, sectionKey }) {
      if (!conversationId || !sectionKey) return
      const agents = state.conversationAgents[conversationId] || []
      const agentId = agents.length ? agents[0].id : null
      if (!agentId) return
      try {
        const data = await api.resetAgentPromptSection(agentId, sectionKey)
        const sections = data && data.sections ? data.sections : null
        if (sections) {
          commit('SET_AGENT_PROMPT_SECTIONS', { conversationId, sections })
        }
      } catch (e) {
        console.warn('[store] resetAgentPromptSection failed:', e.message)
      }
    },

    // GET /conversations/:id/models - 加载会话可用模型列表
    // 用户主动切换模型（保存到后端）
    selectModel({ commit, state }, model) {
      commit('SET_SELECTED_MODEL', model)
      const item = state.sessionItems.find(s => s.key === state.currentSessionKey)
      if (item && item.type === 'agent') {
        const convId = item.conversationId
        const agents = convId ? (state.conversationAgents[convId] || []) : []
        const agentId = agents.length ? agents[0].id : (item.agentId || null)
        if (agentId) {
          api.updateAgent(agentId, { model, think: state.thinkingDepth }).catch(() => {})
        }
      }
    },
    // 用户主动切换思考深度（保存到后端）
    selectThinkingDepth({ commit, state }, depth) {
      commit('SET_THINKING_DEPTH', depth)
      const item = state.sessionItems.find(s => s.key === state.currentSessionKey)
      if (item && item.type === 'agent') {
        const convId = item.conversationId
        const agents = convId ? (state.conversationAgents[convId] || []) : []
        const agentId = agents.length ? agents[0].id : (item.agentId || null)
        if (agentId && state.selectedModel) {
          api.updateAgent(agentId, { model: state.selectedModel, think: depth }).catch(() => {})
        }
      }
    },

    async fetchModels({ commit, getters, state }, conversationId) {
      const id = conversationId || getters.currentConversationId
      if (!id) return
      try {
        const data = await api.getConversationModels(id)
        const list = Array.isArray(data) ? data : (data && Array.isArray(data.data) ? data.data : [])
        if (list.length) {
          const modelIds = list.map(m => m.id || m.name || m)
          commit('SET_MODEL_LIST', modelIds)
          commit('SET_MODEL_DETAILS', list)
          // Agent 私聊会返回 defaultModel（后端保存的用户上次选择的模型）
          const defaultModelId = data && data.defaultModel ? data.defaultModel : null
          if (defaultModelId) {
            commit('SET_SELECTED_MODEL', defaultModelId)
            const defaultModelObj = list.find(m => (m.id || m.name) === defaultModelId)
            if (defaultModelObj && defaultModelObj.defaultThink) {
              commit('SET_THINKING_DEPTH', defaultModelObj.defaultThink)
            }
          }
        }
      } catch (e) {
        console.warn('[store] fetchModels failed:', e.message)
      }
    },

    // 打开模型选择器，同时触发按会话拉取模型列表（仅 Agent 私聊）
    openModelPicker({ commit, dispatch, getters, state }, rect) {
      commit('SET_SKILL_PICKER_OPEN', false)
      commit('SET_MODEL_PICKER_OPEN', { open: true, rect })
    },
    async fetchConversations({ commit, state, dispatch }, { force } = {}) {
      if (!force && state.conversationsLoaded) return
      try {
        const res = await api.getConversations()
        // 兼容两种格式：直接数组 / { conversations } / { data: { conversations } }
        let list
        if (Array.isArray(res)) {
          list = res
        } else if (res && res.data && Array.isArray(res.data.conversations)) {
          list = res.data.conversations  // { code, data: { conversations, total } }
        } else if (res && Array.isArray(res.conversations)) {
          list = res.conversations
        } else {
          list = []
        }
        // 无论有没有数据都 commit，确保 conversationsLoaded=true，空列表时缺省能显示
        commit('SET_API_CONVERSATIONS', list)
      } catch (e) {
        console.warn('[store] fetchConversations failed:', e.message)
        // 失败时也标记 loaded，避免一直 loading
        commit('SET_API_CONVERSATIONS', [])
      }
    },

    // GET /conversations/:id/agents - 加载会话中的 Agent 列表（含 VNC 信息）
    async fetchConversationAgents({ commit }, conversationId) {
      if (!conversationId) return
      try {
        const data = await api.getConversationAgents(conversationId)
        let agents = []
        if (Array.isArray(data)) {
          agents = data
        } else if (data && Array.isArray(data.agents)) {
          agents = data.agents
        }
        commit('SET_CONVERSATION_AGENTS', { conversationId, agents })
      } catch (e) {
        console.warn('[store] fetchConversationAgents failed:', e.message)
      }
    },

    // ── 置顶消息 actions ──
    pinnedKey(_ctx, { conversationId, topicId }) {
      return `${conversationId}::${topicId || 'default'}`
    },
    async fetchPinnedMessages({ commit }, { conversationId, topicId }) {
      if (!conversationId) return
      const key = `${conversationId}::${topicId || 'default'}`
      try {
        const data = await api.getPinnedMessages(conversationId, topicId)
        const items = (data && Array.isArray(data.items)) ? data.items : []
        commit('SET_PINNED_MESSAGES', { key, items })
      } catch (e) {
        console.warn('[store] fetchPinnedMessages failed:', e.message)
      }
    },
    async pinMessage({ commit }, { conversationId, topicId, message }) {
      if (!conversationId || !message || !message.id) return
      const key = `${conversationId}::${topicId || 'default'}`
      // 乐观：先把当前 message 包成 pinItem 加进列表头
      const optimistic = {
        id: 'optimistic-' + message.id,
        messageId: message.id,
        topicId: topicId || null,
        pinnedBy: '',
        pinnedAt: new Date().toISOString(),
        message
      }
      commit('ADD_PINNED_MESSAGE', { key, item: optimistic })
      commit('SET_PINNED_BAR_DISMISSED', { key, dismissed: false })
      try {
        await api.pinMessage(conversationId, message.id, topicId)
        // WS 广播会回声 ADD_PINNED_MESSAGE 覆盖（messageId 去重）
      } catch (e) {
        // 已置顶（400 already pinned）忽略；其它错回滚
        const msg = (e && e.message) || ''
        if (!/already pinned/i.test(msg)) {
          commit('REMOVE_PINNED_MESSAGE', { key, messageId: message.id })
          console.warn('[store] pinMessage failed:', msg)
        }
      }
    },
    async unpinMessage({ commit, state }, { conversationId, topicId, messageId }) {
      if (!conversationId || !messageId) return
      const key = `${conversationId}::${topicId || 'default'}`
      const before = (state.conversationPinnedMessages[key] || []).slice()
      commit('REMOVE_PINNED_MESSAGE', { key, messageId })
      try {
        await api.unpinMessage(conversationId, messageId, topicId)
      } catch (e) {
        // 404 not found 视为成功；其它错回滚
        const msg = (e && e.message) || ''
        if (!/not found/i.test(msg)) {
          commit('SET_PINNED_MESSAGES', { key, items: before })
          console.warn('[store] unpinMessage failed:', msg)
        }
      }
    },

    async fetchWechatBinding({ commit }, agentId) {
      if (!agentId) return
      try {
        const data = await api.getAgentWechat(agentId)
        commit('SET_WECHAT_BINDING', { agentId, binding: data || null })
      } catch (e) {
        commit('SET_WECHAT_BINDING', { agentId, binding: null })
      }
    },

    // GET /conversations/:id/files - 加载某个会话的文件列表
    async fetchConversationFiles({ commit }, { conversationId, topicId, type, page, pageSize, append } = {}) {
      if (!conversationId) return
      commit('SET_CONVERSATION_FILES_LOADING', true)
      try {
        // api.js request() unwraps json.data automatically when code===200
        // so data = { files: [...], total, page, pageSize }
        const data = await api.getConversationFiles(conversationId, {
          topicId,
          type: type || undefined,
          page: page || 1,
          pageSize: pageSize || 50
        })
        // type=link 时后端返回 links 数组，其余返回 files 数组
        const files = type === 'link'
          ? (data && Array.isArray(data.links) ? data.links : [])
          : (data && Array.isArray(data.files)) ? data.files
            : Array.isArray(data) ? data : []
        const total = (data && data.total) || files.length
        commit('SET_CONVERSATION_FILES', { conversationId, files, total, append })
      } catch (e) {
        console.warn('[store] fetchConversationFiles failed:', e.message)
      } finally {
        commit('SET_CONVERSATION_FILES_LOADING', false)
      }
    },

    // GET /conversations/:id/files?type=agent - 获取产物文件列表
    async fetchArtifactFiles({ commit }, { conversationId } = {}) {
      if (!conversationId) return
      try {
        const data = await api.getConversationFiles(conversationId, { type: 'agent', pageSize: 50 })
        const files = (data && Array.isArray(data.files)) ? data.files
          : Array.isArray(data) ? data : []
        commit('SET_CONVERSATION_ARTIFACT_FILES', { conversationId, files })
      } catch (e) {
        console.warn('[store] fetchArtifactFiles failed:', e.message)
      }
    },

    // GET /files/:id/versions - 获取文件版本历史
    async fetchFileVersions({ commit }, { fileId, conversationId, topicId } = {}) {
      if (!fileId || !conversationId) return
      commit('SET_FILE_VERSIONS_LOADING', true)
      try {
        const data = await api.getFileVersions(fileId, { conversationId, topicId })
        commit('SET_FILE_VERSIONS', { fileId, data: data || {} })
      } catch (e) {
        console.warn('[store] fetchFileVersions failed:', e.message)
      } finally {
        commit('SET_FILE_VERSIONS_LOADING', false)
      }
    },

    // GET /topics/:id/folders/tree - 获取话题文件夹树（仅 Agent 会话）
    async fetchTopicFolderTree({ commit }, topicId) {
      if (!topicId) return
      try {
        const data = await api.getTopicFolderTree(topicId)
        commit('SET_TOPIC_FOLDER_TREE', { topicId, data })
      } catch (e) {
        console.warn('[store] fetchTopicFolderTree failed:', e.message)
      }
    },

    // GET /unread-count - 获取账号级未读数
    async fetchPeerOnlineForSession({ commit }, item) {
      const convId = item.conversationId
      if (!convId) return
      try {
        const data = await api.getFriendOnline(convId)
        const online = data && data.online === true
        commit('UPDATE_SESSION_ITEM', { key: item.key, updates: { peerOnline: online } })
      } catch (e) {
        // 静默处理
      }
    },
    async fetchOnlineCount({ commit }, conversationId) {
      if (!conversationId) return
      try {
        const data = await api.getOnlineCount(conversationId)
        const count = data?.data?.totalOnlineCount ?? data?.totalOnlineCount ?? 0
        commit('SET_ONLINE_COUNT', { conversationId, count })
      } catch (e) {
        console.warn('[store] fetchOnlineCount failed:', e.message)
      }
    },

    async fetchUnreadCount({ commit }) {
      try {
        const data = await api.getUnreadCount()
        if (data) {
          commit('SET_TOTAL_UNREAD', {
            total: data.total || 0,
            byConversation: data.byConversation || []
          })
        }
      } catch (e) {
        console.warn('[store] fetchUnreadCount failed:', e.message)
      }
    },

    // GET /conversations/:id/mentionable - 加载可@的成员列表
    async fetchMentionableMembers({ commit }, conversationId) {
      try {
        const data = await api.getMentionableMembers(conversationId)
        const members = Array.isArray(data) ? data : (data && data.members ? data.members : [])
        commit('SET_MENTIONABLE_MEMBERS', { conversationId, members })
      } catch (e) {
        console.warn('[store] fetchMentionableMembers failed:', e.message)
      }
    },

    // GET /conversations/:id/topics - 加载话题列表
    async fetchConversationTopics({ commit, state }, conversationId) {
      try {
        const data = await api.getTopics(conversationId)
        const topics = Array.isArray(data) ? data : []
        commit('SET_CONVERSATION_TOPICS', { conversationId, topics })
        // 提取 context_usage_display（从第一个 topic 的 lastMessage.metadata）
        if (topics.length && topics[0].lastMessage) {
          const meta = topics[0].lastMessage.metadata || topics[0].lastMessage.meta || {}
          if (meta.context_usage_display) {
            commit('SET_CONTEXT_USAGE_DISPLAY', { conversationId, value: meta.context_usage_display })
          }
        }

        // 将 API 话题映射为 sessionTopicPages 格式，驱动话题 UI
        if (topics.length) {
          const TOPIC_COLORS = [
            'linear-gradient(135deg,#F06A6A,#F69A9A)',
            'linear-gradient(135deg,#57B2FF,#7AC8FF)',
            'linear-gradient(135deg,#7B61FF,#A28BFF)',
            'linear-gradient(135deg,#F0A83D,#F5C768)',
            'linear-gradient(135deg,#35B58A,#67D3A9)',
            'linear-gradient(135deg,#4F8CF4,#7EB0FF)'
          ]
          const pageKey = `api-topics-${conversationId}`
          const session = state.sessionItems.find(s => s.conversationId === conversationId)
          // 本地已有话题页时，unread/mentionCount 完全用本地值，不被服务端覆盖
          const existingPage = state.sessionTopicPages[pageKey]
          const existingTopicMap = {}
          if (existingPage) {
            for (const t of existingPage.topics) existingTopicMap[t.key] = t
          }
          const mappedTopics = topics.map((t, i) => {
            const lastMsgText = extractLastMessageFromTopic(t)
            const title = t.name || t.title || 'General'
            const existing = existingTopicMap[t.id]
            // 本地有记录：保留本地 unread/mentionCount；本地没有：用服务端值
            const unread = existing ? existing.unread : (t.unread_count || t.unreadCount || 0)
            const mentionCount = existing ? existing.mentionCount : (t.mention_count || t.mentionCount || 0)
            return {
              key: t.id,
              label: title.length > 6 ? title.slice(0, 6) + '…' : title,
              name: title,
              time: formatRelativeTime(t.lastMessageAt || t.last_message_at || t.createdAt || t.created_at || ''),
              preview: lastMsgText || '暂无最新消息',
              message: lastMsgText || '暂无最新消息',
              unread,
              icon: title.slice(0, 1),
              color: TOPIC_COLORS[i % TOPIC_COLORS.length],
              mentionCount,
              isDefault: t.isDefault || t.is_default || false
            }
          })
          // 保留已存在的 activeKey，不要因为刷新覆盖用户已切换的话题
          const preservedActiveKey = existingPage?.activeKey
          const activeKey = (preservedActiveKey && mappedTopics.find(t => t.key === preservedActiveKey))
            ? preservedActiveKey
            : (mappedTopics[0]?.key || null)
          const page = {
            sessionKey: session ? session.key : conversationId,
            title: session ? session.name : '会话',
            sub: `${mappedTopics.length} 个话题`,
            activeKey,
            topics: mappedTopics
          }
          commit('SET_SESSION_TOPIC_PAGE', { pageKey, page })
          // 更新 sessionItem 的 hasTopics 和 topicKey
          if (session) {
            const isSingleTopic = mappedTopics.length === 1
            let latestMsg = ''
            const byTime = [...topics].sort((a, b) => {
              const ta = a.lastMessageAt || a.last_message_at || ''
              const tb = b.lastMessageAt || b.last_message_at || ''
              return tb.localeCompare(ta)
            })
            for (const t of byTime) {
              const text = extractLastMessageFromTopic(t)
              if (text) { latestMsg = text; break }
            }
            const topicTotalUnread = mappedTopics.reduce((sum, t) => sum + (Number(t.unread) || 0), 0)
            const topicTotalMention = mappedTopics.reduce((sum, t) => sum + (Number(t.mentionCount) || 0), 0)
            const updates = {
              hasTopics: !isSingleTopic,
              topicKey: pageKey,
              lastMessage: latestMsg
            }
            if (topicTotalUnread > (session.unread || 0)) updates.unread = topicTotalUnread
            if (topicTotalMention > (session.mentionCount || 0)) updates.mentionCount = topicTotalMention
            commit('UPDATE_SESSION_ITEM', { key: session.key, updates })
            // 如果这是当前 agent 会话且 currentTopicKey 还没设置，补设并拉 folder tree
            if (session.key === state.currentSessionKey && session.type === 'agent' && !state.currentTopicKey) {
              const firstTopicId = activeKey
              if (firstTopicId) {
                commit('SET_CURRENT_TOPIC_KEY', firstTopicId)
                dispatch('fetchTopicFolderTree', firstTopicId)
              }
            }
          }
        }
      } catch (e) {
        console.warn('[store] fetchConversationTopics failed:', e.message)
      }
    },

    // POST /conversations/:id/topics - 创建新话题
    async createConversationTopic({ commit, state, dispatch }, { conversationId, name }) {
      try {
        const data = await api.createTopic(conversationId, name)
        if (data) {
          // 创建成功后重新拉取话题列表
          await dispatch('fetchConversationTopics', conversationId)
          // 自动切换到新建的话题
          const newTopicId = data.id || data.topicId || data.topic_id
          if (newTopicId) {
            const session = state.sessionItems.find(s => s.conversationId === conversationId)
            const pageKey = session?.topicKey
            if (pageKey) {
              await dispatch('switchTopic', { topicKey: newTopicId, pageKey })
              // 打开话题侧边栏
              commit('SET_TOPICS_VIEW', { open: true, pageKey })
            }
          }
        }
        return data
      } catch (e) {
        console.warn('[store] createConversationTopic failed:', e.message)
        return null
      }
    },

    // GET /conversations/:id/members/available - 获取可邀请的成员列表
    async fetchAvailableMembers({ commit }, conversationId) {
      try {
        const data = await api.getAvailableMembers(conversationId)
        const members = Array.isArray(data) ? data : (data && data.members ? data.members : [])
        commit('SET_AVAILABLE_MEMBERS', { conversationId, members })
      } catch (e) {
        console.warn('[store] fetchAvailableMembers failed:', e.message)
      }
    },

    // POST /conversations/:id/invite - 邀请成员加入群聊
    async inviteMembers({ commit, dispatch }, { conversationId, userIds }) {
      try {
        await api.inviteGroupMembers(conversationId, userIds)
        dispatch('fetchAvailableMembers', conversationId)
        dispatch('fetchMentionableMembers', conversationId)
        commit('INCREMENT_ONLINE_COUNT_VERSION')
      } catch (e) {
        console.warn('[store] inviteMembers failed:', e.message)
      }
    },
    // POST /conversations/:id/agents - 向群组添加 Agent
    async addAgentsToConversation({ commit, dispatch }, { conversationId, agentIds }) {
      try {
        await api.addAgentsToConversation(conversationId, agentIds)
        dispatch('fetchAvailableMembers', conversationId)
        dispatch('fetchMentionableMembers', conversationId)
        dispatch('fetchConversationAgents', conversationId)
        commit('INCREMENT_ONLINE_COUNT_VERSION')
      } catch (e) {
        console.warn('[store] addAgentsToConversation failed:', e.message)
      }
    },

    // 跳转到指定消息（引用消息点击）
    async jumpToMessage({ commit, state, dispatch }, { conversationId, topicId, messageId }) {
      if (!conversationId || !messageId) return false
      console.log('[jumpToMessage]', { messageId, currentRawLen: (state.conversationRawMessages[conversationId] || []).length })
      // 先在已加载的消息里找
      const existing = state.conversationMessages[conversationId] || []
      if (existing.some(m => m.id === messageId)) return true
      // 没找到，用 around 模式拉取，合并到现有 messages 里
      if (!state.currentUserId) await dispatch('fetchCurrentUser')
      try {
        const data = await api.getMessages(conversationId, topicId, { around: messageId, limit: 50 })
        const list = Array.isArray(data) ? data : (data && (data.messages || []))
        if (list && list.length) {
          // 合并：around 拉到的消息 + 当前已有 raw（含 ws 推过来的）按 id 去重 + sort
          // raw 现在已包含 ws 消息，不需要再单独抓 wsAdditions 处理
          const existingRaw = state.conversationRawMessages[conversationId] || []
          const rawIds = new Set(existingRaw.map(m => m && m.id).filter(Boolean))
          // 用 id 集合判断 hasGap：around 段和原 existing 段没有重叠 → 中间有 gap
          const hadExisting = existingRaw.length > 0
          const aroundOverlapsExisting = list.some(m => m && m.id && rawIds.has(m.id))
          const hasGap = hadExisting && !aroundOverlapsExisting
          const byId = new Map()
          existingRaw.forEach(m => { if (m && m.id) byId.set(m.id, m) })
          list.forEach(m => { if (m && m.id) byId.set(m.id, m) })
          const sorted = [...byId.values()].sort(compareMessages)
          const messages = sorted.map(m => mapApiMessageToUi(m, state.currentUserId))
          commit('SET_CONVERSATION_MESSAGES', { conversationId, messages, rawMessages: sorted })
          console.log('[jumpToMessage merged]', { rawLen: sorted.length, hasGap, listLen: list.length })
          Vue.set(state.conversationMessagesHasMore, conversationId, true)
          if (hasGap) {
            // around 段升序最末一条作为 after 锚点
            const aroundSorted = [...list].sort(compareMessages)
            const aroundLast = aroundSorted[aroundSorted.length - 1]
            if (aroundLast && aroundLast.id) {
              Vue.set(state.conversationGapAfterId, conversationId, aroundLast.id)
            }
            // 记录原 existing 的 id 集合（用于衔接判断）
            Vue.set(state.conversationLatestSegmentIds, conversationId, rawIds)
            Vue.set(state.conversationHasMoreAfter, conversationId, true)
            // 异步循环 after 直到衔接 latestSegmentIds 或最多 5 次（不阻塞跳转动画）
            setTimeout(async () => {
              console.log('[setTimeout LOOP START]')
              const waitForLockRelease = () => new Promise(resolve => {
                const tick = () => {
                  if (!state.conversationLoadingAfter[conversationId]) resolve()
                  else setTimeout(tick, 50)
                }
                tick()
              })
              let guard = 5
              while (guard-- > 0 && state.conversationHasMoreAfter[conversationId]) {
                // 锁被别人占用（scroll 监听触发的 dispatch）→ 等其完成再继续
                if (state.conversationLoadingAfter[conversationId]) {
                  console.log('[setTimeout LOOP wait lock]')
                  await waitForLockRelease()
                  // 等完后 hasMoreAfter 可能已经 false（已衔接）
                  if (!state.conversationHasMoreAfter[conversationId]) break
                }
                console.log('[setTimeout LOOP iter]', { guard, hasMoreAfter: state.conversationHasMoreAfter[conversationId], gapAfterId: state.conversationGapAfterId[conversationId] })
                let more
                try {
                  more = await dispatch('loadMessagesAfter', { conversationId, topicId })
                } catch (e) {
                  console.error('[setTimeout LOOP error]', e)
                  break
                }
                console.log('[setTimeout LOOP after dispatch]', { more, hasMoreAfterNow: state.conversationHasMoreAfter[conversationId] })
                // dispatch 返回 false 可能是真的衔接完成，也可能是被锁挡住
                // 通过 hasMoreAfter 区分：still true 说明被挡，循环再试；false 说明真停了
                if (!more && !state.conversationHasMoreAfter[conversationId]) break
              }
              console.log('[setTimeout LOOP END]', { remainingGuard: guard, hasMoreAfter: state.conversationHasMoreAfter[conversationId] })
            }, 0)
          } else {
            Vue.delete(state.conversationGapAfterId, conversationId)
            Vue.delete(state.conversationLatestSegmentIds, conversationId)
            Vue.set(state.conversationHasMoreAfter, conversationId, false)
          }
        }
        return true
      } catch (e) {
        console.warn('[store] jumpToMessage failed:', e.message)
        return false
      }
    },

    // 跳转后向下用 after 游标拉下一段并合并
    async loadMessagesAfter({ commit, state, dispatch }, { conversationId, topicId } = {}) {
      if (!conversationId) return false
      if (state.conversationLoadingAfter[conversationId]) {
        console.log('[loadMessagesAfter LOCKED]', { rawLen: (state.conversationRawMessages[conversationId] || []).length })
        return false
      }
      if (!state.conversationHasMoreAfter[conversationId]) return false
      // 立即设锁（同步），抢占 race
      Vue.set(state.conversationLoadingAfter, conversationId, true)
      const existingRaw = state.conversationRawMessages[conversationId] || []
      console.log('[loadMessagesAfter ENTRY]', { rawLenAtEntry: existingRaw.length, anchorId: state.conversationGapAfterId[conversationId] })
      if (!existingRaw.length) {
        Vue.set(state.conversationLoadingAfter, conversationId, false)
        return false
      }
      // 锚点用 jumpToMessage 时记录的 gapAfterId
      const anchorId = state.conversationGapAfterId[conversationId]
      if (!anchorId) {
        Vue.set(state.conversationHasMoreAfter, conversationId, false)
        Vue.set(state.conversationLoadingAfter, conversationId, false)
        return false
      }
      if (!state.currentUserId) await dispatch('fetchCurrentUser')
      try {
        const anchorMsg = existingRaw.find(m => m && m.id === anchorId)
        const anchorSeq = anchorMsg && anchorMsg.seq
        const data = await api.getMessages(conversationId, topicId, { after: anchorId, limit: 50 })
        const list = Array.isArray(data) ? data : (data && (data.messages || []))
        const hasMore = !!(data && data.hasMore)
        console.log('[loadMessagesAfter]', {
          anchorId, anchorSeq,
          listLen: (list || []).length,
          listFirstId: list && list[0] && list[0].id,
          listFirstSeq: list && list[0] && list[0].seq,
          listLastId: list && list[list.length - 1] && list[list.length - 1].id,
          listLastSeq: list && list[list.length - 1] && list[list.length - 1].seq,
          hasMore,
          listMonotonic: (list || []).every((m, i, arr) => i === 0 || (arr[i - 1].seq || 0) <= (m.seq || 0))
        })
        // 每次 await 后重新读 raw（await 期间 ws 可能已写入）
        const freshRaw = state.conversationRawMessages[conversationId] || []
        const rawIds = new Set(freshRaw.map(m => m && m.id).filter(Boolean))
        const newOnes = (list || []).filter(m => m && m.id && !rawIds.has(m.id))
        console.log('[loadMessagesAfter check]', {
          freshRawLen: freshRaw.length,
          listLen: (list || []).length,
          newOnesLen: newOnes.length,
          listIds: (list || []).slice(0, 3).map(m => m.id?.slice(0, 6)),
          rawIdsSample: [...rawIds].slice(0, 3).map(id => String(id).slice(0, 6))
        })
        if (!list || !list.length || newOnes.length === 0) {
          console.warn('[loadMessagesAfter] EARLY STOP: newOnes=0')
          Vue.set(state.conversationHasMoreAfter, conversationId, false)
          Vue.delete(state.conversationGapAfterId, conversationId)
          Vue.delete(state.conversationLatestSegmentIds, conversationId)
          return false
        }
        // 合并：byId 去重 + sort 按 seq 升序
        const byId = new Map()
        freshRaw.forEach(m => { if (m && m.id) byId.set(m.id, m) })
        list.forEach(m => { if (m && m.id) byId.set(m.id, m) })
        const sorted = [...byId.values()].sort(compareMessages)
        const messages = sorted.map(m => mapApiMessageToUi(m, state.currentUserId))
        commit('SET_CONVERSATION_MESSAGES', { conversationId, messages, rawMessages: sorted })
        console.log('[loadMessagesAfter merged]', {
          newRawLen: sorted.length,
          actualStateRawLen: (state.conversationRawMessages[conversationId] || []).length
        })
        // 衔接判断：拉到的 list 是否包含原最新段任意 id（id 重叠 = 已衔接最新段）
        const latestIds = state.conversationLatestSegmentIds[conversationId] || new Set()
        const reachedLatest = list.some(m => m && m.id && latestIds.has && latestIds.has(m.id))
        console.log('[loadMessagesAfter junction]', { reachedLatest, hasMore, latestIdsSize: latestIds.size })
        if (reachedLatest || !hasMore) {
          Vue.set(state.conversationHasMoreAfter, conversationId, false)
          Vue.delete(state.conversationGapAfterId, conversationId)
          Vue.delete(state.conversationLatestSegmentIds, conversationId)
          return false
        }
        // gap 还在：anchor = list 升序最末 id，继续向 latest 段方向拉
        const sortedNew = [...list].sort(compareMessages)
        const newLast = sortedNew[sortedNew.length - 1]
        if (newLast && newLast.id) {
          Vue.set(state.conversationGapAfterId, conversationId, newLast.id)
        }
        return true
      } catch (e) {
        console.warn('[store] loadMessagesAfter failed:', e.message)
        return false
      } finally {
        Vue.set(state.conversationLoadingAfter, conversationId, false)
      }
    },

    // GET /conversations/:id/messages - 加载某个会话的消息（支持 topicId 筛选）
    async fetchConversationMessages({ commit, state, dispatch }, payload) {
      const conversationId = typeof payload === 'string' ? payload : payload.conversationId
      const topicId = typeof payload === 'string' ? undefined : payload.topicId
      const loadMore = typeof payload === 'object' && payload.loadMore
      if (!conversationId) return
      if (loadMore) {
        Vue.set(state.conversationMessagesLoading, conversationId, true)
      } else {
        state.chatLoading = true
        // 初始/重新加载是最新窗口
        Vue.set(state.conversationHasMoreAfter, conversationId, false)
        Vue.delete(state.conversationGapAfterId, conversationId)
        Vue.delete(state.conversationLatestSegmentIds, conversationId)
      }
      // 在 API 请求前快照未读数（markTopicRead 可能在 API 返回前清零）
      // 有 topicId 时用话题级未读，否则用会话级未读
      const _sessionItem = state.sessionItems.find(s => s.conversationId === conversationId)
      let _snapshotUnread = 0
      let _topicFound = false
      if (topicId && _sessionItem && _sessionItem.topicKey) {
        const _page = state.sessionTopicPages[_sessionItem.topicKey]
        const _topic = _page && _page.topics.find(t => t.key === topicId)
        if (_topic) {
          _topicFound = true
          _snapshotUnread = _topic.unread || 0
        }
      }
      if (!_topicFound && !_snapshotUnread) {
        _snapshotUnread = (_sessionItem && _sessionItem.unread) || state.unreadByConversation[conversationId] || 0
      }
      if (!state.currentUserId) {
        await dispatch('fetchCurrentUser')
      }
      try {
        const PAGE_SIZE = 50
        const existingRaw = loadMore ? (state.conversationRawMessages[conversationId] || []) : []
        // loadMore 用 before=第一条真实消息id（避免新消息插入导致 offset 漂移漏读重复）
        let beforeAnchor = null
        if (loadMore) {
          for (let i = 0; i < existingRaw.length; i++) {
            const m = existingRaw[i]
            if (m && m.id && typeof m.id === 'string' && !m.id.startsWith('temp-') && !m.id.startsWith('day-')) {
              beforeAnchor = m.id
              break
            }
          }
        }
        const data = beforeAnchor
          ? await api.getMessages(conversationId, topicId, { limit: PAGE_SIZE, before: beforeAnchor })
          : await api.getMessages(conversationId, topicId, { limit: PAGE_SIZE, offset: 0 })
        const list = Array.isArray(data) ? data : (data && (data.messages || []))
        const total = data && data.total != null ? data.total : null
        if (list) {
          // before 模式接口返回 ASC，offset 模式返回 DESC（需要 reverse）
          const reversed = beforeAnchor ? [...list] : [...list].reverse()
          const sorted = reversed.sort(compareMessages)
          // 修复 replyTo.text 的 [@#n] 占位符：用同批消息的 body.text 替换
          const idToText = {}
          for (const m of sorted) { if (m.id && m.body) idToText[m.id] = m.body.text || '' }
          for (const m of sorted) {
            if (m.replyTo && m.replyTo.text && m.replyTo.text.includes('[@#') && idToText[m.replyTo.id]) {
              m.replyTo.text = idToText[m.replyTo.id]
            }
          }
          // 修复文件消息 body.text 的 [@#n] 占位符：用 mentionableMembers 查名字
          const allMembers = state.mentionableMembers[conversationId] || []
          for (const m of sorted) {
            if (m.body && m.body.text && m.body.text.includes('[@#') && m.mentions && m.mentions.length) {
              m.body.text = m.body.text.replace(/\[@#(\d+)\]/g, (_, n) => {
                const mn = m.mentions[parseInt(n, 10) - 1]
                if (!mn) return '@'
                // 优先用 mentions 里的 name，没有则从 mentionableMembers 查
                if (mn.name) return '@' + mn.name
                const found = allMembers.find(mb => (mb.id || mb.userId) === mn.id)
                return found ? ('@' + (found.name || found.displayName || '')) : '@'
              })
            }
          }
          const messages = sorted.map(m => mapApiMessageToUi(m, state.currentUserId))
          if (loadMore) {
            // 追加到前面（旧消息在前）
            // 关键：重新读 state.conversationRawMessages（await 期间可能被 loadMessagesAfter 修改）
            // 用 byId 去重防重复，整体 sort 保证顺序
            const freshRaw = state.conversationRawMessages[conversationId] || []
            const byId = new Map()
            sorted.forEach(m => { if (m && m.id) byId.set(m.id, m) })
            freshRaw.forEach(m => { if (m && m.id && !byId.has(m.id)) byId.set(m.id, m) })
            const mergedRaw = [...byId.values()].sort(compareMessages)
            const mergedMessages = mergedRaw.map(m => mapApiMessageToUi(m, state.currentUserId))
            commit('SET_CONVERSATION_MESSAGES', { conversationId, messages: mergedMessages, rawMessages: mergedRaw })
            console.log('[fetchConversationMessages loadMore]', { freshRawLen: freshRaw.length, sortedLen: sorted.length, mergedRawLen: mergedRaw.length })
          } else {
            commit('SET_CONVERSATION_MESSAGES', { conversationId, messages, rawMessages: sorted })
            console.log('[fetchConversationMessages initial]', { newRawLen: sorted.length })
          }
          // hasMore 判断：before 模式优先用响应里的 hasMore；offset 模式按 total
          let hasMore
          if (beforeAnchor) {
            hasMore = data && data.hasMore != null ? !!data.hasMore : (list.length >= PAGE_SIZE)
          } else {
            hasMore = total != null ? (list.length < total) : (list.length >= PAGE_SIZE)
          }
          Vue.set(state.conversationMessagesHasMore, conversationId, hasMore)
          // 保存被@的消息ID列表（仅首次加载，不是loadMore）
          if (!loadMore) {
            const mIds = data && Array.isArray(data.mentionedMsgIds) ? data.mentionedMsgIds : []
            Vue.set(state.mentionedMsgIds, conversationId, mIds)
            Vue.set(state.mentionJumpIndex, conversationId, 0)
            // 保存第一条未读消息ID：优先用后端字段，否则用快照 unreadCount 从列表倒推
            let _fuid = (data && data.firstUnreadMessageId) || (data && data.first_unread_message_id) || null
            // 验证后端返回的 ID 在当前消息列表中（后端返回的是会话级而非话题级）
            if (_fuid) {
              const fuMsg = messages.find(m => m.id === _fuid)
              if (!fuMsg) _fuid = null
              else if (fuMsg.side === 'right') _fuid = null
            }
            if (!_fuid && messages.length && _snapshotUnread > 0 && _snapshotUnread <= messages.length) {
              _fuid = messages[messages.length - _snapshotUnread].id
            }
            const _fuidKey = topicId ? conversationId + ':' + topicId : conversationId
            Vue.set(state.firstUnreadMessageId, _fuidKey, _fuid)
          }
          // agent 会话：有消息→processing，无消息→empty
          if (!loadMore) {
            const currentItem = state.sessionItems.find(s => s.key === state.currentSessionKey)
            if (currentItem && currentItem.type === 'agent' && currentItem.conversationId === conversationId) {
              commit('SET_MAIN_STATE', sorted.length > 0 ? 'processing' : 'empty')
            }
          }
        }
      } catch (e) {
        console.warn('[store] fetchConversationMessages failed:', e.message)
      } finally {
        if (loadMore) {
          Vue.set(state.conversationMessagesLoading, conversationId, false)
        } else {
          state.chatLoading = false
        }
      }
    },

    // PUT /topics/:id/read - 标记 Topic 已读
    async markTopicRead({ commit, state, dispatch }, topicId) {
      if (!topicId) return
      // 记录本地已读，防止 fetchConversationTopics 用服务端数据覆盖
      Vue.set(state.localReadTopicIds, topicId, true)
      // 立即本地清零该话题的未读和@计数，并重新汇总到 sessionItem
      for (const pageKey of Object.keys(state.sessionTopicPages)) {
        const page = state.sessionTopicPages[pageKey]
        const topicIdx = page.topics.findIndex(t => t.key === topicId)
        if (topicIdx >= 0) {
          const updatedTopics = page.topics.map(t =>
            t.key === topicId ? { ...t, unread: 0, mentionCount: 0 } : t
          )
          commit('SET_SESSION_TOPIC_PAGE', { pageKey, page: { ...page, topics: updatedTopics } })
          // 重新汇总该会话所有话题的未读数，更新 sessionItem
          const session = state.sessionItems.find(s => s.key === page.sessionKey)
          if (session) {
            const totalUnread = updatedTopics.reduce((sum, t) => sum + (Number(t.unread) || 0), 0)
            const totalMention = updatedTopics.reduce((sum, t) => sum + (Number(t.mentionCount) || 0), 0)
            commit('UPDATE_SESSION_ITEM', { key: session.key, updates: { unread: totalUnread || 0, mentionCount: totalMention } })
          }
          break
        }
      }
      try {
        await api.markTopicRead(topicId)
        dispatch('fetchUnreadCount')
      } catch (e) {
        console.warn('[store] markTopicRead failed:', e.message)
      }
    },

    // POST /conversations/:id/messages - 发送消息（带乐观更新）
    async sendConversationMessage({ commit, state, getters }, { conversationId, text, skipOptimistic, replyToId, replyTo }) {
      if (!conversationId) return
      const _cmd = getters.selectedCommand
      if (!text.trim() && !_cmd) return
      const displayText = text.trim()

      // 乐观 typing 触发条件：agent 私聊 + 当前消息列表为空（agent 第一次被叫醒）
      // agent 后端初始化慢（加载 oc 等），首次响应 push agent.reply_start 可能要 1-5s 才到
      // 发送成功瞬间立即 commit WS_AGENT_TYPING → 左侧出现 agent 三个点
      // 真实 WS agent.reply_start 来时同 mutation 覆盖此 key，无缝替换；reply_end / message.new 自动清
      const isAgentSession = state.currentSessionType === 'agent'
      const beforeMsgs = state.conversationMessages[conversationId] || []
      const shouldOptimisticTyping = isAgentSession && beforeMsgs.length === 0
      const topicIdAtSend = state.currentTopicKey || null

      // 用 pendingMentions 的 key 直接在文本里查找替换为 [@#n] 占位符
      const mentions = []
      const matched = []
      let counter = 0
      let sendText = displayText
      // 按名字长度降序排列，防止短名字先匹配到长名字的前缀
      const pendingNames = Object.keys(state.pendingMentions).sort((a, b) => b.length - a.length)
      for (const name of pendingNames) {
        const pending = state.pendingMentions[name]
        if (!pending) continue
        const search = '@' + name
        let pos = 0
        while ((pos = sendText.indexOf(search, pos)) !== -1) {
          counter++
          matched.push({ name, index: counter, mention: pending })
          mentions.push({ id: pending.id, type: pending.type || 'user' })
          sendText = sendText.substring(0, pos) + `[@#${counter}]` + sendText.substring(pos + search.length)
          pos += `[@#${counter}]`.length
        }
      }

      // 命令前置到消息
      const selectedCmd = getters.selectedCommand
      if (selectedCmd) sendText = sendText ? `${selectedCmd} ${sendText}` : selectedCmd

      // 气泡显示文本也带上命令
      const bubbleText = selectedCmd ? (displayText ? `${selectedCmd} ${displayText}` : selectedCmd) : displayText

      const selectedSkillsList = (getters.selectedSkills || []).map(s => ({ id: s.id, name: s.name }))
      const tempMsg = {
        id: 'temp-' + Date.now(),
        seq: 0,
        readCount: 0,
        memberCount: 0,
        side: 'right',
        name: '',
        text: bubbleText,
        rawText: sendText,
        mentions: mentions.length ? matched.map(m => ({ id: m.mention.id, type: m.mention.type, name: m.name })) : [],
        skills: selectedSkillsList,
        createdAt: new Date().toISOString(),
        time: formatTime(new Date().toISOString()),
        avatarText: 'Me',
        avatarBackground: 'linear-gradient(135deg,#6F63FF,#5B8DEF)',
        kind: 'text',
        replyTo: replyTo || null
      }
      if (!skipOptimistic) {
        commit('APPEND_CONVERSATION_MESSAGE', { conversationId, message: tempMsg, topicId: _safeTopicKey(state, conversationId) })
      }
      commit('CLEAR_PENDING_MENTIONS')

      try {
        const skillIds = state.currentSessionType === 'agent' ? (getters.selectedSkillIds || []) : []
        const sendResult = await api.sendMessage(conversationId, {
          text: sendText,
          topicId: _safeTopicKey(state, conversationId),
          replyToId: replyToId || undefined,
          mentions: mentions.length ? mentions : undefined,
          model: state.currentSessionType === 'agent' ? (getters.selectedModel || undefined) : undefined,
          think: state.currentSessionType === 'agent' ? (state.thinkingDepth || undefined) : undefined,
          skills: skillIds.length ? skillIds : undefined
        })
        // 用后端返回的真实 id 替换 temp id，防止 WS 推送时重复
        const realId = sendResult && sendResult.id ? sendResult.id : null
        const realSeq = sendResult && sendResult.seq ? sendResult.seq : 0
        commit('UPDATE_MESSAGE_STATUS', { conversationId, messageId: tempMsg.id, status: 'sent', ...(realId ? { id: realId } : {}), seq: realSeq, readCount: 0, memberCount: 0 })
        if (selectedCmd) commit('SET_SESSION_COMMAND', null)
        if (skillIds.length) commit('CLEAR_SKILLS')
        // 发送成功 → 立即乐观显示 agent 三个点（agent 私聊首条场景）
        // 真实 agent 名取 state.conversations 的原始 c.name（不带 -agentRoleName 后缀），
        // 跟 WS push 上来的 chunk.agentName 格式对齐，避免徽章 [Agent] 跟 name "xxx-Agent" 后缀重复
        if (shouldOptimisticTyping) {
          const item = state.sessionItems.find(s => s.conversationId === conversationId)
          const conv = state.conversations.find(c => c.id === conversationId)
          const realAgentName = (conv && conv.name) || (item && item.name) || 'Agent'
          commit('WS_AGENT_TYPING', {
            conversationId,
            topicId: topicIdAtSend,
            agentId: (item && item.agentId) || '',
            agentName: realAgentName,
            avatarUrl: (item && item.avatar) || (conv && conv.avatar) || null
          })
        }
      } catch (e) {
        console.warn('[store] sendConversationMessage failed:', e.message)
        commit('UPDATE_MESSAGE_STATUS', { conversationId, messageId: tempMsg.id, status: 'failed' })
        // 发送失败 → 清掉乐观 typing（如果触发了）
        if (shouldOptimisticTyping) {
          commit('CLEAR_AGENT_TYPING', { conversationId, topicId: topicIdAtSend })
        }
      }
    },

    async deleteMessage({ commit }, { conversationId, messageId }) {
      // 乐观删除，先直接从本地移除
      commit('REMOVE_CONVERSATION_MESSAGE', { conversationId, messageId })
      try {
        await api.deleteMessage(conversationId, messageId)
      } catch (e) {
        console.warn('[store] deleteMessage failed:', e.message)
        // 如果接口失败可考虑恢复，目前保持乐观删除
      }
    },
    async resendMessage({ commit, state, getters }, { conversationId, message }) {
      commit('UPDATE_MESSAGE_STATUS', { conversationId, messageId: message.id, status: 'sending' })
      try {
        if (message.kind === 'text' || !message.kind) {
          // 重发要把首次发送时附带的技能/模型/思考深度等也带上, 否则后端拿不到技能 ID, 跟首发行为不一致
          // tempMsg 上已经保存了 skills 数组([{id,name}]) 和 replyTo, mentions 取 rawText 之外的结构化字段
          const skillIds = (message.skills || []).map(s => s.id).filter(Boolean)
          const isAgentSession = state.currentSessionType === 'agent'
          await api.sendMessage(conversationId, {
            text: message.rawText || message.text,
            topicId: _safeTopicKey(state, conversationId),
            replyToId: (message.replyTo && message.replyTo.id) || undefined,
            mentions: (message.mentions && message.mentions.length) ? message.mentions : undefined,
            model: isAgentSession ? (getters.selectedModel || undefined) : undefined,
            think: isAgentSession ? (state.thinkingDepth || undefined) : undefined,
            skills: skillIds.length ? skillIds : undefined
          })
        } else {
          // 文件类消息重发：不重新上传，直接重发已有 url
          const files = (message.files || []).filter(f => f.url)
          if (files.length) {
            await api.sendMediaMessage(conversationId, {
              messageType: 'file',
              body: { files, caption: message.text || '' },
              topicId: _safeTopicKey(state, conversationId)
            })
          }
        }
        commit('UPDATE_MESSAGE_STATUS', { conversationId, messageId: message.id, status: 'sent' })
      } catch (e) {
        console.warn('[store] resendMessage failed:', e.message)
        commit('UPDATE_MESSAGE_STATUS', { conversationId, messageId: message.id, status: 'failed' })
      }
    },

    setActiveSession({ commit, state, dispatch }, payload) {
      // 兼容旧的字符串调用和新的对象调用 { sessionKey, targetTopicId }
      const sessionKey = typeof payload === 'string' ? payload : payload.sessionKey
      const targetTopicId = typeof payload === 'object' ? payload.targetTopicId : null
      const item = state.sessionItems.find(s => s.key === sessionKey)
      if (!item) return
      const isSameSession = state.currentSessionKey === sessionKey
      // 切会话时关闭置顶面板（只在真正切换时关，避免同会话再次激活也强行关）
      if (!isSameSession && state.pinnedPanelOpen) commit('SET_PINNED_PANEL_OPEN', false)
      commit('SET_CURRENT_SESSION', { key: sessionKey, type: item.type })
      commit('SET_WORKSPACE_OPEN', true)
      // 切换到不同会话时重置预览和 tab；同一会话保持不变（路由切换回来不丢失预览）
      if (!isSameSession) {
        commit('SET_GROUP_WORKSPACE_PREVIEW', null)
        // 已停用 agent: 云电脑/全部文件/变更 tab 被隐藏, 默认直接进「交付物」(否则 cloud 面板会渲染但 tab 不见)
        const isDisabledAgent = item.type === 'agent' && item.myAgentStatus === 'disabled'
        const defaultTab = isDisabledAgent ? 'artifacts'
          : (item.type === 'agent' || item.hasAgent) ? 'cloud'
          : 'files'
        commit('SET_GROUP_WORKSPACE_TAB', defaultTab)
        // 关闭话题详情视图，回到会话列表
        if (state.topicsViewOpen) {
          commit('SET_TOPICS_VIEW', { open: false })
        }
      }

      // 如果是 API 真实会话（有 conversationId），加载消息、Agent 列表、文件列表、可@成员和话题
      if (item.conversationId) {
        // targetTopicId 优先（从话题 tab 点击进来），否则取页面当前 activeKey
        const topicPage = item.topicKey ? state.sessionTopicPages[item.topicKey] : null
        const activeTopicId = targetTopicId || topicPage?.activeKey || null
        // 同步设置 currentTopicKey，保证 folder tree / 消息 computed 能拿到正确值
        commit('SET_CURRENT_TOPIC_KEY', activeTopicId)
        const msgPromise = activeTopicId
          ? dispatch('fetchConversationMessages', { conversationId: item.conversationId, topicId: activeTopicId })
          : dispatch('fetchConversationMessages', item.conversationId)
        // 消息列表加载完成后再标记已读（确保 mentionedMsgIds 已拿到）
        msgPromise.then(() => {
          if (activeTopicId) dispatch('markTopicRead', activeTopicId)
        })
        const agentsPromise = dispatch('fetchConversationAgents', item.conversationId)
        // 拉取置顶消息（topic 级）
        dispatch('fetchPinnedMessages', { conversationId: item.conversationId, topicId: activeTopicId })
        // 文件列表改为懒加载：用户切到工作区 files/artifacts tab 时由 GroupWorkspacePanel 触发
        dispatch('fetchMentionableMembers', item.conversationId)
        dispatch('fetchConversationTopics', item.conversationId).then(() => {
          // 话题加载完成后拉取群聊任务计划（需要 topicId）
          if (item.type === 'group' && item.hasAgent) {
            const topicId = state.currentTopicKey
              || (state.sessionTopicPages[item.topicKey] && state.sessionTopicPages[item.topicKey].activeKey)
            if (topicId) dispatch('fetchGroupPlans', { conversationId: item.conversationId, topicId })
          }
        })
        // 拉取会话可用模型列表 & Agent 命令列表（仅 Agent 私聊）
        if (item.type === 'agent') {
          dispatch('fetchModels', item.conversationId)
          // 等 agents 列表拉取完成后再拉命令列表和技能列表（合并一次请求）
          agentsPromise.then(() => {
            if (state.currentSessionKey !== sessionKey) return
            dispatch('fetchAgentCapabilitiesAndSkills', item.conversationId)
            const agents = state.conversationAgents[item.conversationId] || []
            const agentId = agents.length ? agents[0].id : null
            if (agentId) dispatch('fetchWechatBinding', agentId)
          })
          if (activeTopicId) dispatch('fetchTopicFolderTree', activeTopicId)
        }
        // 拉取在线人数（群组/同事会话）
        if (item.type !== 'agent') {
          dispatch('fetchOnlineCount', item.conversationId)
        }
        // 非 agent 非 group 的私聊查询对方在线状态
        if (item.type !== 'agent' && item.type !== 'group') {
          dispatch('fetchPeerOnlineForSession', item)
        }
      }

      if (item.type === 'agent') {
        // API 会话有消息时进入 processing 态展示消息列表，否则 empty 态
        const hasMessages = item.conversationId && (
          (state.conversationRawMessages[item.conversationId] || []).length > 0 ||
          (state.conversationMessages[item.conversationId] || []).length > 0
        )
        const agentState = hasMessages ? 'processing' : 'empty'
        commit('SET_MAIN_STATE', agentState)
      } else {
        commit('SET_MAIN_STATE', 'empty')
      }
    },

    startFakeFlow({ commit, dispatch }, rawQuery) {
      const query = normalizeDemoQuery(rawQuery)
      const shortQuery = shortenDemoQuery(query, 14)
      const topic = extractDemoTopic(query)
      const isAutonomous = /自主\s*Agent|Autonomous\s*Agents?/i.test(query) || /自主\s*Agent|Autonomous\s*Agents?/i.test(topic)
      const fileName = makeReportFileName(query, isAutonomous)

      commit('SET_TELEGRAM_PROMPT', query)
      commit('SET_TELEGRAM_PENDING_REPLY', null)
      commit('SET_SELECTED_SKILLS', [])
      commit('CLEAR_SKILLS')
      commit('SET_MAIN_STATE', 'processing')
      commit('SET_WORKSPACE_SCENE', 'processing')
      commit('SET_WORKSPACE_OPEN', true)
      commit('SET_FAKE_FLOW_RUNNING', true)

      commit('SET_PROCESSING_CONTENT', {
        userBubble: query,
        agentReply: `好的，我将为您调研并整理目前主流的${topic}，请稍候。`,
        progressTitle: `搜索：${shortQuery}`,
        searchDesc: `目前已向用户确认想研究${topic}，下一步将会直接搜集主流${topic}的主要信息与特点。`,
        searchChip1: `搜索目前主流的${topic}列表和分类`,
        searchChip2: `访问相关网页以获取主流${topic}的详细功能和最新进展`,
        searchChip3: `搜索 吴彦祖Agent 和 Devin AI 的最新信息及竞争对手`,
        resultDesc: `已整理出主流${topic}（如 LangGraph、AutoGen、CrewAI、OpenAI Agents SDK、MetaGPT 等）的功能特点，涉及状态管理、多Agent协作、任务调度等。`,
        resultChip1: `阅读之前保存的网页内容，获取${topic}相关框架与方案的详细信息`,
        resultChip2: `创建Markdown文件，用于整理${topic}的调研结果`
      })

      commit('SET_WORKSPACE_RESULT_DATA', {
        fileName,
        title: isAutonomous ? '自主Agent（Autonomous Agents）调研报告' : `${shortenDemoQuery(query, 20)} 调研报告`,
        summary: `随着人工智能技术的快速发展，${topic}正成为 AI 领域的重要方向。这类 Agent 能够围绕目标进行规划、执行、反馈和学习，在更少人工干预下完成复杂任务。本报告对当前主流的${topic}进行系统梳理，概述其分类、功能特点与代表产品。`,
        coreHeading: `${topic}的定义与核心特征`,
        sectionTitle: `主流${topic}分类与产品介绍`,
        sectionDesc: `根据功能和应用场景的不同，当前的${topic}可以分为工作流编排型、多 Agent 协作型以及产品化执行型。OpenAI Operator、Anthropic Claude、Manus AI、Devin AI 等代表产品，在自动执行、浏览器操作、任务分解和跨工具协同方面各具优势。`
      })

      commit('SET_WORKSPACE_BROWSER', {
        address: 'https://www.google.com/',
        title: query,
        desc: `准备在 Google 首页上搜索与"${query}"相关的内容。`
      })

      // Auto-complete after delay
      dispatch('completeFakeFlowAfterDelay')
    },

    completeFakeFlowAfterDelay({ commit }) {
      setTimeout(() => {
        commit('SET_MAIN_STATE', 'result')
        commit('SET_WORKSPACE_SCENE', 'result')
        commit('SET_FAKE_FLOW_RUNNING', false)
      }, 1800)
    },

    completeFakeFlow({ commit }) {
      commit('SET_MAIN_STATE', 'result')
      commit('SET_WORKSPACE_SCENE', 'result')
      commit('SET_FAKE_FLOW_RUNNING', false)
    },

    openGroupCloudFloat({ commit }, mode) {
      commit('SET_GROUP_CLOUD_FLOAT', { active: true, mode })
      commit('SET_WORKSPACE_FLOAT', true)
    },

    closeGroupCloudFloat({ commit }) {
      commit('SET_GROUP_CLOUD_FLOAT', { active: false, mode: null })
      commit('SET_WORKSPACE_FLOAT', false)
    },

    openWorkspaceFullscreen({ commit }) {
      commit('SET_WORKSPACE_FLOAT', true)
      commit('SET_WORKSPACE_TAKEOVER', false)
    },

    openWorkspaceTakeover({ commit }) {
      commit('SET_WORKSPACE_FLOAT', true)
      commit('SET_WORKSPACE_TAKEOVER', true)
    },

    closeWorkspaceFullscreen({ commit }) {
      commit('SET_WORKSPACE_FLOAT', false)
      commit('SET_WORKSPACE_TAKEOVER', false)
    },

    openAgentInsightModal({ commit, state }, type) {
      commit('SET_AGENT_INSIGHT_MODAL', {
        open: true,
        type,
        sessionKey: state.currentSessionKey
      })
    },

    closeAgentInsightModal({ commit }) {
      commit('SET_AGENT_INSIGHT_MODAL', { open: false })
    },

    openAgentTaskBubble({ commit, state }, coords) {
      commit('SET_AGENT_TASK_BUBBLE', { open: true, sessionKey: state.currentSessionKey, ...(coords || { x: null, y: null }) })
    },

    closeAgentTaskBubble({ commit }) {
      commit('SET_AGENT_TASK_BUBBLE', { open: false })
    },

    openAgentTaskPreview({ commit, state, dispatch }, taskId) {
      commit('SET_AGENT_TASK_PREVIEW', { open: true, sessionKey: state.currentSessionKey, id: taskId, detail: null, detailLoading: true })
      dispatch('fetchAgentTaskDetail', taskId)
    },

    closeAgentTaskPreview({ commit }) {
      commit('SET_AGENT_TASK_PREVIEW', { open: false })
    },

    async fetchAgentTaskDetail({ commit, state }, jobId) {
      const item = state.sessionItems.find(s => s.key === state.currentSessionKey)
      if (!item) { commit('SET_AGENT_TASK_PREVIEW', { detailLoading: false }); return }
      const conversationId = item.conversationId
      const agents = conversationId ? (state.conversationAgents[conversationId] || []) : []
      const agentId = item.agentId || (agents.length ? agents[0].id : null)
      if (!agentId || !jobId) { commit('SET_AGENT_TASK_PREVIEW', { detailLoading: false }); return }
      try {
        const data = await api.getAgentScheduledTaskDetail(agentId, jobId)
        if (data) {
          const gw = data.gatewayDetail || {}
          const gwJob = gw.job || {}
          const gwSchedule = gwJob.schedule || {}
          const gwPayload = gwJob.payload || {}
          const gwLastRun = gw.last_run || {}
          const gwJobState = gwJob.state || {}
          const detail = {
            id: data.jobId || gw.job_id || jobId,
            title: data.taskName || gwJob.name || '',
            agentName: data.agentName || '',
            agentId: data.agentId || agentId,
            fileCount: data.fileCount || 0,
            files: (data.files || []).map(f => ({
              id: f.id,
              title: f.fileName || f.file_name || f.name || '',
              url: f.url || f.fileUrl || f.file_url || '',
              size: f.fileSize || f.file_size || 0,
              type: f.fileType || f.file_type || 'file',
              createdAt: f.createdAt || f.created_at || ''
            })),
            taskState: gw.task_state || '',
            enabled: gwJob.enabled !== false,
            scheduleKind: gwSchedule.kind || '',
            scheduleCron: gwSchedule.expr || gwSchedule.cron || '',
            scheduleEveryMinutes: gwSchedule.everyMinutes || null,
            scheduleDisplay: gw.schedule_display || '',
            payloadKind: gwPayload.kind || '',
            payloadMessage: gwPayload.message || '',
            payloadText: gwPayload.text || '',
            lastRunAtMs: gwLastRun.runAtMs || null,
            lastRunStatus: gwJobState.lastRunStatus || gwJobState.lastStatus || gwLastRun.status || '',
            lastRunTs: gwLastRun.ts || gwJobState.lastRunAtMs || null,
            lastRunSummary: gwLastRun.summary || '',
            lastRunError: gwLastRun.error || gwJobState.lastError || '',
            lastRunDurationMs: gwLastRun.durationMs || gwJobState.lastDurationMs || null,
            nextRunAtMs: gwJobState.nextRunAtMs || gwLastRun.nextRunAtMs || null,
            resultText: gw.result_text || gwLastRun.result_text || '',
            resultSource: gw.result_source || gwLastRun.result_source || '',
            consecutiveErrors: gwJobState.consecutiveErrors || 0,
            runs: (gw.runs || []).map(r => ({
              ts: r.ts,
              status: r.status || '',
              summary: r.summary || '',
              error: r.error || '',
              durationMs: r.durationMs || null
            })),
            steps: data.steps || gw.steps || []
          }
          commit('SET_AGENT_TASK_PREVIEW', { detail, detailLoading: false })
        } else {
          commit('SET_AGENT_TASK_PREVIEW', { detail: { id: jobId, title: '', agentName: '', fileCount: 0, files: [], runs: [] }, detailLoading: false })
        }
      } catch (e) {
        console.error('[store] fetchAgentTaskDetail failed:', e)
        commit('SET_AGENT_TASK_PREVIEW', { detail: null, detailLoading: false })
      } finally {
        // 确保 loading 一定关闭
        if (state.agentTaskPreview.detailLoading) {
          commit('SET_AGENT_TASK_PREVIEW', { detailLoading: false })
        }
      }
    },

    openAgentDetailPopover({ commit, state, dispatch }, coords) {
      commit('SET_AGENT_DETAIL_POPOVER', { open: true, sessionKey: state.currentSessionKey, ...(coords || {}) })
      dispatch('fetchAgentScheduledTasks')
    },

    closeAgentDetailPopover({ commit }) {
      commit('SET_AGENT_DETAIL_POPOVER', { open: false })
    },

    async fetchAgentScheduledTasks({ commit, state }) {
      const sessionKey = state.currentSessionKey
      const item = state.sessionItems.find(s => s.key === sessionKey)
      if (!item) return
      const conversationId = item.conversationId
      const agents = conversationId ? (state.conversationAgents[conversationId] || []) : []
      const agentId = item.agentId || (agents.length ? agents[0].id : null)
      if (!agentId) return
      Vue.set(state.agentDetailPopover, 'loading', true)
      try {
        const topicId = state.currentTopicKey || undefined
        const data = await api.getAgentScheduledTasks(agentId, { conversationId, topicId })
        const tasks = (data && data.tasks ? data.tasks : []).map(t => ({
          id: t.jobId || t.id,
          title: t.name || '未命名任务',
          status: t.lastRunStatus === 'success' ? '已完成' : t.lastRunStatus === 'error' ? '异常' : t.taskState === 'running' ? '运行中' : t.enabled ? '待执行' : '已停用',
          frequency: t.source || '',
          lastRun: t.lastRunAt ? new Date(t.lastRunAt).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—',
          resultText: t.resultText || '',
          enabled: t.enabled,
          taskState: t.taskState,
          isDeleted: t.isDeleted,
          totalRuns: t.totalRuns
        }))
        Vue.set(state.agentTasksData, sessionKey, tasks)
      } catch (e) {
        console.warn('[store] fetchAgentScheduledTasks failed:', e.message)
      } finally {
        Vue.set(state.agentDetailPopover, 'loading', false)
      }
    },

    setTopicsView({ commit }, { open, pageKey }) {
      commit('SET_TOPICS_VIEW', { open, pageKey })
    },

    // 切换话题并加载对应消息
    async switchTopic({ commit, state, dispatch }, { topicKey, pageKey }) {
      // 切话题也关掉置顶面板（不同话题对应不同的置顶列表）
      if (state.pinnedPanelOpen) commit('SET_PINNED_PANEL_OPEN', false)
      commit('SET_CURRENT_TOPIC_KEY', topicKey)
      if (pageKey) {
        commit('SET_TOPIC_PAGE_ACTIVE_KEY', { pageKey, topicKey })
      }
      const page = state.sessionTopicPages[pageKey]
      const sessionKey = page?.sessionKey
      const session = sessionKey ? state.sessionItems.find(s => s.key === sessionKey) : null
      const conversationId = session?.conversationId
      if (!conversationId) return

      // 切话题：拉新话题的置顶消息
      dispatch('fetchPinnedMessages', { conversationId, topicId: topicKey })

      if (session.type === 'agent') {
        dispatch('fetchTopicFolderTree', topicKey)
        const cachedMsgs = (state.conversationRawMessages[conversationId] || []).filter(
          m => (m.topicId || m.topic_id) === topicKey
        )
        if (cachedMsgs.length > 0) {
          const mappedCached = cachedMsgs.map(m => mapApiMessageToUi(m, state.currentUserId))
          commit('SET_CONVERSATION_MESSAGES', { conversationId, messages: mappedCached, rawMessages: cachedMsgs })
          commit('SET_MAIN_STATE', 'processing')
          dispatch('fetchConversationMessages', { conversationId, topicId: topicKey }).then(() => {
            dispatch('markTopicRead', topicKey)
          })
        } else {
          commit('SET_MAIN_STATE', 'empty')
          commit('SET_CONVERSATION_MESSAGES', { conversationId, messages: [], rawMessages: [] })
          await dispatch('fetchConversationMessages', { conversationId, topicId: topicKey })
          dispatch('markTopicRead', topicKey)
          if (state.currentSessionKey !== session.key) return
          const loaded = (state.conversationRawMessages[conversationId] || []).filter(
            m => (m.topicId || m.topic_id) === topicKey
          )
          commit('SET_MAIN_STATE', loaded.length > 0 ? 'processing' : 'empty')
        }
        return
      }

      // 非 agent 会话：先加载消息，再标记已读（确保未读快照正确）
      dispatch('fetchConversationMessages', { conversationId, topicId: topicKey }).then(() => {
        dispatch('markTopicRead', topicKey)
      })
    },

    // 获取联系人 + Agent 列表（用于创建群聊选人）
    async fetchTeamMembers({ commit }) {
      try {
        const data = await api.getTeamMembers()
        const list = data && data.members ? data.members : (Array.isArray(data) ? data : [])
        const members = list.map(m => ({
          userId: m.id,
          type: m.type || 'account',
          name: m.name || m.username || m.displayName || m.display_name || '',
          avatarUrl: m.avatarUrl || null,
          avatarColorRgb: m.avatarColorRgb || '',
          email: m.email || '',
          status: m.onlineStatus || m.status || '',
          position: m.positionName || m.position_name || '',
          department: m.departmentName || m.department_name || '',
          note: m.note || m.description || '',
          account: m.account || m.email || '',
          isSelf: m.isSelf || false,
          tokenUsage: m.tokenUsage || null,
          agentCount: m.agentCount || 0,
          agents: (m.agents || []).map(a => ({
            id: a.id,
            name: a.name || '',
            avatarUrl: a.avatarUrl || null,
            avatarType: a.avatarType || '',
            avatarColorHsl: a.avatarColorHsl || '',
            avatarColorRgb: a.avatarColorRgb || '',
            avatarEmoji: a.avatarEmoji || '',
            roleName: a.roleName || '',
            status: a.status || '',
            busyStatus: a.busyStatus || 'idle'
          }))
        }))
        commit('SET_TEAM_MEMBERS', members)
      } catch (e) {
        console.warn('[store] fetchTeamMembers failed:', e.message)
      }
    },

    // 获取 Agent 列表用于邀请弹窗（不写入 teamMembers，直接返回数组）
    async fetchAgentsForInvite(_, conversationId) {
      try {
        const data = conversationId
          ? await api.getAvailableAgentsForConversation(conversationId)
          : await api.getAgents()
        const list = Array.isArray(data) ? data : (data && data.agents ? data.agents : (data && data.data) || [])
        return list.map(a => ({
          id: a.id,
          name: a.name || '未命名Agent',
          avatarUrl: a.avatarUrl || null,
          avatarColorRgb: a.avatarColorRgb || '',
          description: a.description || '',
          roleName: a.roleName || a.role_name || '',
          status: a.status || 'idle'
        }))
      } catch (e) {
        console.warn('[store] fetchAgentsForInvite failed:', e.message)
        return []
      }
    },

    // 获取联系人列表（仅 account，用于发起私聊）
    async fetchDirectContacts({ commit }) {
      try {
        const data = await api.getContacts('account')
        const list = Array.isArray(data) ? data : (data && data.data) || []
        commit('SET_DIRECT_CONTACTS', list)
      } catch (e) {
        console.warn('[store] fetchDirectContacts failed:', e.message)
      }
    },

    // 创建私聊（调用真实接口）
    async createDirectSession({ state, dispatch }, { participantId }) {
      try {
        const resp = await api.createConversation({
          type: 'direct',
          participantIds: [participantId]
        })
        // 从 resp.conversation.id 取新会话 id
        const conv = resp && (resp.conversation || resp.data?.conversation || resp)
        const newId = conv && (conv.id || conv.conversationId) ||
          (resp && (resp.id || resp.conversationId || resp.data?.id))
        await dispatch('fetchConversations', { force: true })
        if (newId) {
          dispatch('setActiveSession', newId)
        }
      } catch (e) {
        console.warn('[store] createDirectSession failed:', e.message)
      }
    },

    // 创建群聊（调用真实接口）
    async createGroupSession({ commit, state, dispatch }, { name, participantIds, agentIds }) {
      try {
        console.log('[createGroupSession] participant_ids:', participantIds, 'agent_ids:', agentIds)
        const resp = await api.createConversation({
          type: 'group',
          name: name || '新群聊',
          participantIds: participantIds || [],
          agentIds: agentIds || []
        })
        const conv = resp && (resp.conversation || resp.data?.conversation || resp)
        const newId = conv && (conv.id || conv.conversationId) ||
          (resp && (resp.id || resp.conversationId || resp.data?.id))
        await dispatch('fetchConversations', { force: true })
        if (newId) {
          dispatch('setActiveSession', newId)
        }
      } catch (e) {
        console.warn('[store] createGroupSession failed:', e.message)
      }
    },

    async exitGroupSession({ commit, state, dispatch }, sessionKey) {
      const item = state.sessionItems.find(s => s.key === sessionKey)
      if (!item || !item.conversationId) return
      try {
        await api.leaveConversation(item.conversationId)
        const wasActive = state.currentSessionKey === sessionKey
        const pageKey = item.topicKey
        // 从列表移除
        state.sessionItems = state.sessionItems.filter(s => s.key !== sessionKey)
        // 清掉这个群的话题页缓存（避免残留）
        if (pageKey && state.sessionTopicPages[pageKey]) {
          Vue.delete(state.sessionTopicPages, pageKey)
        }
        // 若正卡在这个群的话题列表视图 → 关掉并清空 pageKey（否则那一栏一直挂着退掉的群）
        if (state.currentTopicsPageKey === pageKey) {
          commit('SET_TOPICS_VIEW', { open: false })
          state.currentTopicsPageKey = null
        }
        // 当前正在看这个会话 → 切到剩余第一个会话（走完整 setActiveSession，连带关话题视图/重置预览）；无剩余则清空
        if (wasActive) {
          const next = state.sessionItems[0]
          if (next) {
            dispatch('setActiveSession', next.key)
          } else {
            state.currentSessionKey = null
            commit('SET_TOPICS_VIEW', { open: false })
            state.currentTopicsPageKey = null
          }
        }
        // 退群后立即刷新账号级总未读，否则退群的未读会一直挂在 totalUnread 上，等轮询才消失
        dispatch('fetchUnreadCount')
      } catch (e) {
        console.warn('[store] exitGroupSession failed:', e.message)
        throw e
      }
    }
  }
})